// Full SceneBuilder — phase 2 port from oq-images.
// Drops: style variants, ASCII renderer, agent chat, A* link routing,
// undo/redo (deferred), seed profiles beyond the starter.
//
// Owns:
// - Three.js scene + render loop
// - Click/drag/select/transform/duplicate
// - Link mode (right-angle cables with shader flow)
// - Camera modes (orbit / pan / static, with manual orbit drag)
// - Profile CRUD UI + JSON import/export buttons
// - Catalog palette (left sidebar)
// - Settings panel (right sidebar)
// - Theme toggle, dot grid, float-shadow, PNG export

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as THREE from "three";
import { RoomEnvironment } from "three-stdlib";
import {
  THEMES,
  applyTheme,
  type SceneLights,
  setupSceneLights,
  type ThemeMode,
} from "./lib/sceneAssets";
import {
  applyOneQodeShader,
  getInstanceOrPlaceholder,
  loadManifest,
  type CatalogEntry,
  type Manifest,
} from "./lib/assetRegistry";
import {
  CAMERA_VIEWS,
  type LinkConnection,
  type SceneObject,
  type SceneSettings,
  uid,
} from "./lib/types";
import { DEFAULT_SETTINGS } from "./lib/presets";
import { copyToClipboard, saveImage } from "./lib/exportScene";
import { attachFloatShadow } from "./lib/floatShadow";
import { rebuildLinks, tickLinks } from "./lib/links";
import { applyBrandMark, clearBrandMark } from "./lib/brandMark";
import { applyAccentOverride, clearAccentOverride, normalizeAccentCss } from "./lib/accent";
import {
  alignBottom, alignCenterX, alignCenterZ, alignLeft, alignRight, alignTop,
  applyPatches, distributeHorizontally, distributeVertically,
  type AlignmentResult,
} from "./lib/align";
import {
  addVirtualBrandEntries,
  applyBrandCanvasAppearance,
  applyBrandCanvasTransform,
  createTextLabelObject,
  defaultBrandObjectPatch,
  disposeGeneratedObject,
  isBrandCanvasKind,
  isTextLabelKind,
  resolveBrandCanvasMaterialMode,
  resolveBrandMarkMaterialMode,
  textObjectTag,
} from "./lib/brandObjects";
import {
  addVirtualRackRowEntry,
  createRackRowObject,
  defaultRackRowPatch,
  disposeRackRowObject,
  isRackRowKind,
  rackRowTag,
} from "./lib/rackRow";
import {
  createGuidesGroup,
  defaultGuideSettings,
  dragCoordForRay,
  hitTestGuide,
  rebuildGuides,
} from "./lib/guides";
import type { Guide } from "./lib/types";
import { SelectionCard, type SelectionCardHandle } from "./SelectionCard";
import { Tabs } from "./Tabs";
import { registerBlinkInstance, tickBlink } from "./lib/blink";
import { applyLightingVersion, applyLightingV4Extras, loadLightingVersion, saveLightingVersion, type LightingVersion } from "./lib/lightingVersion";
import { Toast, type ToastState } from "./Toast";
import {
  defaultMainRef,
  deleteLocalProfile,
  deleteServerProfile,
  fetchLocalProfile,
  fetchServerProfile,
  isLocalEditor,
  listLocalProfiles,
  listServerProfiles,
  makeAutoSaver,
  makeLocalRef,
  makeServerRef,
  newLocalSlug,
  readActiveRef,
  renameLocalProfile,
  saveLocalProfile,
  slugFromName,
  writeActiveRef,
  type ProfileRef,
} from "./lib/profileStore";
import type { SceneKind } from "./data/sceneKinds";

// ============================================================
// Helpers
// ============================================================

const CAMERA_PITCH_MIN = 0.02;
const CAMERA_PITCH_MAX = Math.PI / 2 - 0.05;
const DOT_GRID_OPACITY_MAX = 4;
// Slider bounds for the dot-grid radius as a fraction of the cell pitch.
// Lower bound (0.001) is below typical screen-space pixel pitch at common
// camera distances; the renderer floors at 0.5 px so a dot is always at
// least one anti-aliased pixel.
const DOT_SIZE_MIN = 0.001;
const DOT_SIZE_MAX = 0.10;
const DOT_SIZE_DEFAULT = 0.015;

function formatCameraHeight(pitch: number): string {
  const t = THREE.MathUtils.clamp(
    (pitch - CAMERA_PITCH_MIN) / (CAMERA_PITCH_MAX - CAMERA_PITCH_MIN),
    0,
    1,
  );
  if (t <= 0.03) return "Ground";
  if (t >= 0.97) return "Top";
  return `${Math.round(t * 100)}%`;
}

function resolveAccent(s: SceneSettings): string {
  return normalizeAccentCss(s.theme === "dark" ? s.accentDark : s.accentLight);
}
// Default link color is monochrome — white-ish on dark, near-black on light.
// Profiles that want the accent-blue cables can set `linkColorDark` explicitly.
function resolveLinkColor(s: SceneSettings): string {
  const v = s.theme === "dark" ? s.linkColorDark : s.linkColorLight;
  if (v) return v;
  return s.theme === "dark" ? "#dadbe0" : "#1a1a1d";
}
// Default dot color is white-ish on dark, dark grey on light.
function resolveDotColor(s: SceneSettings): number {
  const v = s.theme === "dark" ? s.dotColorDark : s.dotColorLight;
  if (v) return new THREE.Color(v).getHex();
  return s.theme === "dark" ? 0xffffff : 0x3a3a3e;
}

function dotGridOpacityValue(s: SceneSettings): number {
  return s.theme === "dark" ? s.dotGridOpacityDark : s.dotGridOpacityLight;
}

function formatDotOpacity(v: number): string {
  return `${Math.round(v * 100)}%`;
}

function applyDotGridOpacity(dotMat: THREE.MeshBasicMaterial, s: SceneSettings): void {
  const strength = THREE.MathUtils.clamp(dotGridOpacityValue(s), 0, DOT_GRID_OPACITY_MAX);
  // Dark theme: dots are bright on a dark floor. Generous cap (90%) so the
  // slider has full range — set 200% in your saved profile if you want a
  // strong grid, 50% for subtle.
  dotMat.opacity = s.theme === "dark"
    ? Math.min(strength * 0.45, 0.90)
    : Math.min(strength, 1);

  // Material opacity is effectively capped at 1 by alpha blending. For values
  // above 100%, boost the dot color toward the highest-contrast endpoint so
  // the slider can keep making the grid more visible.
  const boost = s.theme === "dark"
    ? 0
    : THREE.MathUtils.clamp(strength - 1, 0, DOT_GRID_OPACITY_MAX - 1);
  const base = new THREE.Color(THEMES[s.theme].dot);
  const hasCustomDotColor = s.theme === "dark" ? Boolean(s.dotColorDark) : Boolean(s.dotColorLight);
  const target = new THREE.Color(s.theme === "light" && !hasCustomDotColor ? 0x000000 : 0xffffff);
  dotMat.color.lerpColors(base, target, boost);
}

function buildDotGridTexture(
  worldSize: number,
  spacing: number,
  hex: number,
  sizeFactor: number = DOT_SIZE_DEFAULT,
): THREE.CanvasTexture {
  const cellsPerAxis = Math.max(2, Math.round(worldSize / spacing));
  // 8192-px canvas: doubles the linear resolution from the previous 4096
  // so the same world-space dot factor renders with twice as many pixels
  // of radius, letting the slider go an order of magnitude smaller before
  // hitting the AA floor. ~64 MB texture (RGBA at 8192²), within budget
  // for any modern GPU and small enough to upload once and reuse via
  // texture caching.
  const px = 8192;
  const canvas = document.createElement("canvas");
  canvas.width = px; canvas.height = px;
  const c = canvas.getContext("2d")!;
  c.clearRect(0, 0, px, px);
  const col = new THREE.Color(hex);
  c.fillStyle = `rgba(${(col.r * 255) | 0}, ${(col.g * 255) | 0}, ${(col.b * 255) | 0}, 1)`;
  const step = px / cellsPerAxis;
  const clampedFactor = THREE.MathUtils.clamp(sizeFactor, DOT_SIZE_MIN, DOT_SIZE_MAX);
  // Floor at 0.5 px — the smallest a 2D-canvas arc can render without
  // disappearing. Below this the dot vanishes (which is what the user
  // wants past a certain point: "off"). The doubled canvas + lower floor
  // means the slider's lower half now covers a useful range instead of
  // snapping to the same minimum dot.
  const r = Math.max(0.5, step * clampedFactor);
  for (let i = 0; i < cellsPerAxis; i++) {
    for (let j = 0; j < cellsPerAxis; j++) {
      const x = i * step + step / 2;
      const y = j * step + step / 2;
      c.beginPath();
      c.arc(x, y, r, 0, Math.PI * 2);
      c.fill();
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

// Initial state when the Main (Reiss) profile JSON hasn't loaded yet. This
// is shown for ~1 frame on cold load; the fetch effect below replaces it.
function bootstrapEmpty(): {
  settings: SceneSettings;
  objects: SceneObject[];
  links: LinkConnection[];
} {
  return {
    settings: DEFAULT_SETTINGS,
    objects: [],
    links: [],
  };
}

// ============================================================
// Component
// ============================================================

export default function SceneBuilder() {
  // ---------- profile + scene state ----------
  // Single Main (Reiss) profile loaded from web/public/profiles/scene-main.json.
  // Edits are persisted only on localhost via the Vite dev save endpoint.
  const boot = useMemo(() => bootstrapEmpty(), []);
  const [settings, setSettings] = useState<SceneSettings>(boot.settings);
  const [objects, setObjects] = useState<SceneObject[]>(boot.objects);
  const [links, setLinks] = useState<LinkConnection[]>(boot.links);
  // Multi-select: array preserves selection order so the most-recently
  // selected id (the "active" one) is at the tail. `selectedId` is derived
  // for the sidebar / single-target callbacks; helpers below treat the
  // entire array as the selection set.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Bumped by the world-sync effect every time it mounts at least one
  // new GLB instance. The link-rebuild effect depends on it so links
  // re-render once the instances they reference actually exist (the
  // initial mount races: state-driven effects run before async GLB
  // loads complete, so the first link-rebuild misses everything).
  const [instancesVersion, setInstancesVersion] = useState(0);
  const selectedId: string | null = selectedIds.length > 0
    ? selectedIds[selectedIds.length - 1]
    : null;
  const setSelectedId = (id: string | null) => {
    setSelectedIds(id == null ? [] : [id]);
  };
  const [linkingFromId, setLinkingFromId] = useState<string | null>(null);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  // Screen-space position of the floating label for the selected object.
  // null = no selection or off-screen. Updated every frame by the render loop.
  const [labelPos, setLabelPos] = useState<{ x: number; y: number } | null>(null);
  const labelPosRef = useRef<{ x: number; y: number } | null>(null);
  const selectionCardRef = useRef<SelectionCardHandle | null>(null);
  // Lighting profile (V1 = original, V2 = matte-velvet ref-image vibe).
  // Stored at module level so the toggle persists across reloads + tabs.
  const [lightingVersion, setLightingVersionState] = useState<LightingVersion>(() => loadLightingVersion());
  const setLightingVersion = useCallback((v: LightingVersion) => {
    setLightingVersionState(v);
    saveLightingVersion(v);
  }, []);
  // Captures the base scene.environmentIntensity at mount so applyLightingVersion
  // can compute v2 as a multiplier of the dark-theme default rather than a
  // hardcoded number that drifts when the theme palette is tweaked.
  const baseEnvIntensityRef = useRef<number>(THEMES.dark.environmentIntensity);
  const [profileLoaded, setProfileLoaded] = useState<boolean>(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [toast, setToast] = useState<ToastState>(null);
  const editor = isLocalEditor();
  const autoSaverRef = useRef(makeAutoSaver());
  // Undo / redo. Snapshots are { objects, links } only; camera and theme
  // changes are intentionally excluded so the history isn't polluted by
  // every drag of a slider. A snapshot is pushed BEFORE each user mutation
  // (the snap-then-mutate pattern). On undo/redo we use a flag to skip
  // the autosave + snapshot side-effects for a single tick.
  type Snap = { objects: SceneObject[]; links: LinkConnection[] };
  const undoStackRef = useRef<Snap[]>([]);
  const redoStackRef = useRef<Snap[]>([]);
  const HISTORY_LIMIT = 100;
  const pushSnapshot = useCallback(() => {
    undoStackRef.current.push({
      objects: objectsRef.current,
      links: linksRef.current,
    });
    if (undoStackRef.current.length > HISTORY_LIMIT) undoStackRef.current.shift();
    // Any new edit invalidates the redo branch.
    redoStackRef.current = [];
  }, []);
  const undo = useCallback(() => {
    const prev = undoStackRef.current.pop();
    if (!prev) return;
    redoStackRef.current.push({
      objects: objectsRef.current,
      links: linksRef.current,
    });
    setObjects(prev.objects);
    setLinks(prev.links);
    // Clear selection so we don't end up pointing at a deleted id.
    setSelectedIds([]);
  }, []);
  const redo = useCallback(() => {
    const next = redoStackRef.current.pop();
    if (!next) return;
    undoStackRef.current.push({
      objects: objectsRef.current,
      links: linksRef.current,
    });
    setObjects(next.objects);
    setLinks(next.links);
    setSelectedIds([]);
  }, []);
  // Active profile reference. On first load this is whatever we last picked
  // for this view (localStorage), falling back to Main (Reiss).
  const [activeRef, setActiveRef] = useState<ProfileRef>(() =>
    readActiveRef("scene") ?? defaultMainRef("scene"));
  const [serverList, setServerList] = useState<Array<{ slug: string; kind: "scene" | "iso" }>>([]);
  const [localList, setLocalList] = useState(() => listLocalProfiles("scene"));
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ---------- refs (kept in sync with state for the render loop) ----------
  const settingsRef = useRef(settings);  useEffect(() => { settingsRef.current = settings; }, [settings]);
  const objectsRef = useRef(objects);    useEffect(() => { objectsRef.current = objects; }, [objects]);
  const linksRef = useRef(links);        useEffect(() => { linksRef.current = links; }, [links]);
  const selectedIdsRef = useRef(selectedIds); useEffect(() => { selectedIdsRef.current = selectedIds; }, [selectedIds]);
  // Compatibility: render-loop / pointer code that already reads "the active id".
  const selectedIdRef = useRef(selectedId); useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  const linkingFromIdRef = useRef(linkingFromId); useEffect(() => { linkingFromIdRef.current = linkingFromId; }, [linkingFromId]);

  // ---------- mount + render refs ----------
  const mountRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const guidesRef = useRef<THREE.Group | null>(null);
  const [selectedGuideId, setSelectedGuideId] = useState<string | null>(null);
  // Tabbed grouping for the right-side panel: keeps Dot grid / Guides /
  // Links visually grouped without cluttering the panel. Local-only state;
  // not persisted (fresh "Dots" pick on reload).
  type DisplayTab = "dots" | "guides" | "links";
  const [displayTab, setDisplayTab] = useState<DisplayTab>("dots");
  const selectedGuideIdRef = useRef<string | null>(null);
  useEffect(() => { selectedGuideIdRef.current = selectedGuideId; }, [selectedGuideId]);
  // Active guide drag (started in onPointerDown).
  const guideDragRef = useRef<{ id: string; axis: "x" | "z"; pointerId: number } | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const worldRef = useRef<THREE.Group | null>(null);
  const linksGroupRef = useRef<THREE.Group | null>(null);
  const groundRef = useRef<THREE.Mesh | null>(null);
  const dotGridRef = useRef<THREE.Mesh | null>(null);
  const lightsRef = useRef<SceneLights | null>(null);
  // One outline mesh per selected id. Tracking an array of helpers (instead
  // of a single one) keeps the disposal symmetric for multi-select.
  const selectionOutlinesRef = useRef<THREE.Box3Helper[]>([]);
  const pivotRef = useRef<THREE.Group | null>(null);
  // Last user-set orbit pivot (shift+right-click). Separate from camTargetRef
  // because panning moves the camera target by design, but the visual pivot
  // marker should stay anchored at the point the user picked.
  const pivotPointRef = useRef<THREE.Vector3 | null>(null);
  const camDraggingRef = useRef<boolean>(false);
  const pivotFlashUntilRef = useRef<number>(0);

  // Live camera state — refs so the render loop sees changes immediately.
  // settings.camera is the persisted snapshot; refs hold the mutable state
  // during interaction and get committed back on pointer-up.
  const camTargetRef = useRef(new THREE.Vector3(
    settings.camera.targetX,
    settings.camera.targetY,
    settings.camera.targetZ,
  ));
  const camYawRef = useRef(settings.camera.yaw);
  const camPitchRef = useRef(settings.camera.pitch);
  const camDistanceRef = useRef(settings.camera.distance);

  // Sync refs from settings whenever they change externally (preset views,
  // profile switch, etc.)
  useEffect(() => {
    camTargetRef.current.set(
      settings.camera.targetX,
      settings.camera.targetY,
      settings.camera.targetZ,
    );
    camYawRef.current = settings.camera.yaw;
    camPitchRef.current = settings.camera.pitch;
    camDistanceRef.current = settings.camera.distance;
  }, [
    settings.camera.targetX,
    settings.camera.targetY,
    settings.camera.targetZ,
    settings.camera.yaw,
    settings.camera.pitch,
    settings.camera.distance,
  ]);

  // Dragging state. `anchorId` is the object the user grabbed; we compute
  // a (dx, dz) delta from where its plane-projected pointer was at start,
  // then apply that delta to every object in `ids` (all initially selected).
  // Storing each id's start position avoids accumulating float drift while
  // the pointer keeps moving.
  const draggingRef = useRef<{
    anchorId: string;
    offsetX: number;
    offsetZ: number;
    starts: Map<string, { x: number; z: number }>;
  } | null>(null);
  // Blender-style camera nav state. mode is decided on pointerdown based on
  // which mouse button + modifiers; refs hold the starting configuration so
  // a drag can continue smoothly until pointerup.
  const camDragRef = useRef<{
    mode: "orbit" | "pan";
    startX: number;
    startY: number;
    startYaw: number;
    startPitch: number;
    startTarget: THREE.Vector3;
    orbitPivot?: THREE.Vector3;
    orbitTargetOffset?: THREE.Vector3;
  } | null>(null);

  // ---------- load manifest ----------
  useEffect(() => {
    loadManifest().then(setManifest).catch((e) => setError(String(e)));
  }, []);

  // ---------- discover server + local profiles for the picker ----------
  useEffect(() => {
    void (async () => {
      const all = await listServerProfiles();
      setServerList(all.filter((e) => e.kind === "scene"));
    })();
    setLocalList(listLocalProfiles("scene"));
  }, []);

  // ---------- load active profile when ref changes ----------
  useEffect(() => {
    let cancelled = false;
    setProfileLoaded(false);
    writeActiveRef(activeRef);
    (async () => {
      type ScenePayload = {
        version: number;
        kind: "scene-profile";
        profile: {
          settings: SceneSettings;
          objects: SceneObject[];
          links: LinkConnection[];
        };
      };
      let payload: ScenePayload | null = null;
      if (activeRef.source === "server") {
        payload = await fetchServerProfile<ScenePayload>(activeRef.slug);
      } else {
        payload = fetchLocalProfile<ScenePayload>("scene", activeRef.slug);
      }
      if (cancelled) return;
      if (payload?.profile) {
        // Merge over DEFAULT_SETTINGS so older saved profiles still get any
        // fields we've added since (e.g. linkCableOpacity, linkPower).
        // Camera is also a defensive merge so partial saves don't NaN out.
        const incoming = payload.profile.settings ?? ({} as Partial<SceneSettings>);
        const merged: SceneSettings = {
          ...DEFAULT_SETTINGS,
          ...incoming,
          camera: { ...DEFAULT_SETTINGS.camera, ...(incoming.camera ?? {}) },
        };
        setSettings(merged);
        setObjects(payload.profile.objects ?? []);
        setLinks(payload.profile.links ?? []);
      }
      setSelectedId(null);
      setProfileLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [activeRef]);

  // ---------- subscribe to autosave status ----------
  useEffect(() => {
    return autoSaverRef.current.subscribe((s) => setSaveStatus(s));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 1800);
    return () => window.clearTimeout(timer);
  }, [toast?.id]);

  // ---------- autosave on edits ----------
  // Server profiles only save on localhost; local profiles always save.
  useEffect(() => {
    if (!profileLoaded) return;
    if (activeRef.source === "server" && !editor) return;
    const payload = {
      version: 2,
      kind: "scene-profile" as const,
      profile: {
        id: activeRef.slug,
        name: activeRef.name,
        settings,
        objects,
        links,
        updatedAt: Date.now(),
      },
    };
    autoSaverRef.current.trigger(activeRef, payload);
    if (activeRef.source === "local") {
      // Refresh sidebar list after local save (debounced flush will fire too).
      setLocalList(listLocalProfiles("scene"));
    }
  }, [settings, objects, links, profileLoaded, editor, activeRef]);

  // ---------- boot Three.js ----------
  useEffect(() => {
    if (!manifest || !mountRef.current) return;
    const mount = mountRef.current;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
      alpha: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Match the reference hero render: deep near-black satin with a quiet
    // PMREM. ACES adds the soft highlight rolloff missing from the flat editor
    // render while material envMapIntensity keeps the bodies dark.
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.10;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setClearColor(0x0d0e10);
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // PMREM environment is used at low intensity per-material so the satin
    // surfaces just barely reveal their corners under the key light.
    const pmrem = new THREE.PMREMGenerator(renderer);
    const envScene = RoomEnvironment();
    const envMap = pmrem.fromScene(envScene, 0.04).texture;
    scene.environment = envMap;
    scene.environmentIntensity = THEMES.dark.environmentIntensity;
    baseEnvIntensityRef.current = THEMES.dark.environmentIntensity;
    pmrem.dispose();
    // Apply the active lighting profile so V2 (if persisted) takes effect
    // on first paint instead of waiting for a re-render via the effect.
    applyLightingVersion(renderer, scene, lightingVersion, baseEnvIntensityRef.current);

    const camera = new THREE.PerspectiveCamera(settings.camera.fov, 1, 0.1, 500);
    cameraRef.current = camera;

    // Ground — plain matte plastic. No procedural shader hook (the dot grid
    // overlay above carries the surface detail; routing the ground through
    // applyOneQodeShader produced visible cell-grid artefacts). Slightly
    // brighter base so the dot grid contrasts against the floor.
    const groundGeo = new THREE.PlaneGeometry(settings.groundSize, settings.groundSize);
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x12151b,
      roughness: 0.92,
      metalness: 0,
      envMapIntensity: 0.10,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.name = "ground";
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
    groundRef.current = ground;

    // Dot grid
    const dotGeo = new THREE.PlaneGeometry(settings.groundSize, settings.groundSize);
    const dotMat = new THREE.MeshBasicMaterial({
      map: buildDotGridTexture(settings.groundSize, settings.dotGridSpacing, resolveDotColor(settings), settings.dotGridSize),
      transparent: true,
      depthWrite: false,
      opacity: Math.min(dotGridOpacityValue(settings), 1),
    });
    const dotGrid = new THREE.Mesh(dotGeo, dotMat);
    dotGrid.name = "dot-grid";
    dotGrid.rotation.x = -Math.PI / 2;
    dotGrid.position.y = 0.005;
    scene.add(dotGrid);
    dotGridRef.current = dotGrid;

    // Lights
    const lights = setupSceneLights(scene);
    lightsRef.current = lights;

    // Apply theme
    applyTheme(settings.theme, scene, renderer, ground, dotMat, lights.hemi, lights.key, lights.fill, lights.rim, lights.floorGlow);
    applyDotGridOpacity(dotMat, settings);

    // World group + links group
    const world = new THREE.Group();
    world.name = "world";
    scene.add(world);
    worldRef.current = world;

    const linksGroup = new THREE.Group();
    linksGroup.name = "links";
    scene.add(linksGroup);
    linksGroupRef.current = linksGroup;

    // Guides overlay (draggable on-floor reference lines + crosses).
    const guides = createGuidesGroup();
    scene.add(guides);
    guidesRef.current = guides;
    rebuildGuides(guides, settingsRef.current, selectedGuideIdRef.current);

    // Pivot indicator — a small dot at the camera's current target. Mostly
    // visible during interaction so users can see where orbit / frame is
    // centered. Sized in world units; the render loop scales it with distance
    // so it stays roughly the same size on screen.
    const pivot = new THREE.Group();
    pivot.name = "pivot";
    const dotMatPivot = new THREE.MeshBasicMaterial({ color: 0x4a8eff, transparent: true, opacity: 0.95, depthTest: false });
    dotMatPivot.userData.baseOpacity = 0.95;
    const pivotDot = new THREE.Mesh(new THREE.SphereGeometry(0.08, 12, 8), dotMatPivot);
    pivotDot.renderOrder = 999;
    pivot.add(pivotDot);
    const ringMatPivot = new THREE.MeshBasicMaterial({ color: 0x4a8eff, transparent: true, opacity: 0.6, side: THREE.DoubleSide, depthTest: false });
    ringMatPivot.userData.baseOpacity = 0.6;
    const pivotRing = new THREE.Mesh(new THREE.RingGeometry(0.18, 0.22, 24), ringMatPivot);
    pivotRing.renderOrder = 999;
    pivotRing.rotation.x = -Math.PI / 2;
    pivot.add(pivotRing);
    pivot.visible = false;
    scene.add(pivot);
    pivotRef.current = pivot;

    // ---------- pointer interactions ----------
    const raycaster = new THREE.Raycaster();
    const mouseNDC = new THREE.Vector2();
    const dragPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    const planeHit = new THREE.Vector3();
    const worldUp = new THREE.Vector3(0, 1, 0);

    function pickInstance(clientX: number, clientY: number): THREE.Object3D | null {
      const rect = renderer.domElement.getBoundingClientRect();
      mouseNDC.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(mouseNDC, camera);
      const hits = raycaster.intersectObjects(world.children, true);
      for (const h of hits) {
        let cur: THREE.Object3D | null = h.object;
        while (cur && !cur.userData?.id && cur.parent) cur = cur.parent;
        if (cur?.userData?.id) return cur;
      }
      return null;
    }

    function getPlaneAt(clientX: number, clientY: number): THREE.Vector3 | null {
      const rect = renderer.domElement.getBoundingClientRect();
      mouseNDC.set(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(mouseNDC, camera);
      if (raycaster.ray.intersectPlane(dragPlane, planeHit)) {
        return planeHit.clone();
      }
      return null;
    }

    function getSelectedPivot(): THREE.Vector3 | null {
      const sel = selectedIdRef.current;
      const worldGroup = worldRef.current;
      if (!sel || !worldGroup) return null;
      const obj = worldGroup.children.find((c) => c.userData?.id === sel);
      if (!obj) return null;
      const box = new THREE.Box3().setFromObject(obj);
      const center = new THREE.Vector3();
      box.getCenter(center);
      return center;
    }

    // Helpers for Blender-style camera nav.
    // Decide whether a pointerdown should grab the camera, and in which mode.
    // User mapping:
    //   - Right-mouse drag  → orbit
    //   - Middle-mouse drag → pan
    //   - Alt + Left drag on empty canvas   → orbit (trackpad fallback)
    //   - Cmd/Ctrl + Left drag on empty canvas → pan (laptop fallback)
    //   - Shift + Alt + Left on empty canvas → pan (trackpad fallback)
    //   - Two-finger trackpad pinch arrives as wheel events with ctrlKey
    function camModeFor(e: PointerEvent): "orbit" | "pan" | null {
      if (e.button === 1) return "pan";                                       // middle
      // Shift+right is reserved for "set pivot" — don't enter orbit.
      if (e.button === 2 && !e.shiftKey) return "orbit";                      // right
      if (e.button === 0 && (e.metaKey || e.ctrlKey) && !e.altKey) return "pan"; // cmd/ctrl+left
      if (e.button === 0 && e.altKey) return e.shiftKey ? "pan" : "orbit";    // alt+left
      return null;
    }

    // Pan moves the camera target along the camera's local right axis (in
    // world XZ) and the world up axis. Using the camera's local up would make
    // shift-pan slide diagonally any time pitch>0; using world up keeps "up
    // is up" always.
    function panTarget(dx: number, dy: number, distance: number) {
      const pixelToWorld = distance * 0.0015;
      // Natural / iOS-style direct-manipulation pan: drag the mouse and
      // the floor follows the cursor. Implemented by moving the orbit
      // target *against* the drag (cursor right → target left → scene
      // appears to move right with the cursor). Always XZ — Y is locked
      // to the start Y so the camera never lifts off the floor.
      const right = new THREE.Vector3();
      const fwd = new THREE.Vector3();
      camera.matrixWorld.extractBasis(right, new THREE.Vector3(), fwd);
      // Three.js camera "z basis" points BEHIND the camera, so the forward
      // direction is its negation.
      fwd.negate();
      right.y = 0; right.normalize();
      fwd.y   = 0; fwd.normalize();
      const target = camDragRef.current!.startTarget.clone();
      // Direct-manipulation pan, both axes:
      //   cursor-RIGHT → camera moves LEFT → floor appears to slide right
      //                  with the cursor.
      //   cursor-DOWN  → camera moves FORWARD on the floor → floor appears
      //                  to slide down toward the viewer with the cursor.
      // Equivalent to grabbing the floor under the cursor and shoving it.
      target.addScaledVector(right, -dx * pixelToWorld);
      target.addScaledVector(fwd,    dy * pixelToWorld);
      target.y = camDragRef.current!.startTarget.y;
      camTargetRef.current.copy(target);
    }

    function onPointerDown(e: PointerEvent) {
      // Shift+right-click: set the orbit pivot to the y=0 floor point under
      // the cursor. Right-click intentionally so the gesture passes through
      // objects without selecting them. Pivot persists in the active
      // profile via settings.camera.target.
      if (e.button === 2 && e.shiftKey) {
        e.preventDefault();
        const p = getPlaneAt(e.clientX, e.clientY);
        if (p) {
          setSettings((prev) => ({
            ...prev,
            camera: { ...prev.camera, targetX: p.x, targetY: 0, targetZ: p.z },
          }));
          // Remember this point for the visible pivot marker. Distinct from
          // camTargetRef so subsequent pans (which move the camera target)
          // don't drag the marker around.
          pivotPointRef.current = new THREE.Vector3(p.x, 0, p.z);
          // Flash the pivot indicator briefly so the user sees the new pivot.
          pivotFlashUntilRef.current = performance.now() + 1200;
        }
        return;
      }
      // Camera nav buttons that never target objects.
      const isLeftCameraShortcut = e.button === 0 && (e.altKey || e.metaKey || e.ctrlKey);
      const mode = camModeFor(e);
      if (mode && !isLeftCameraShortcut) {
        e.preventDefault();
        (renderer.domElement as HTMLCanvasElement).setPointerCapture?.(e.pointerId);
        const orbitPivot = mode === "orbit" ? getSelectedPivot() : null;
        const startTarget = camTargetRef.current.clone();
        camDragRef.current = {
          mode,
          startX: e.clientX,
          startY: e.clientY,
          startYaw: camYawRef.current,
          startPitch: camPitchRef.current,
          startTarget,
          orbitPivot: orbitPivot ?? undefined,
          orbitTargetOffset: orbitPivot ? startTarget.clone().sub(orbitPivot) : undefined,
        };
        camDraggingRef.current = true;
        return;
      }
      if (e.button !== 0) return;
      (renderer.domElement as HTMLCanvasElement).setPointerCapture?.(e.pointerId);

      // ----- Guides: click-select + drag (before object pick so guide lines
      // win over objects underneath). Alt-drag duplicates the guide.
      const gGroup = guidesRef.current;
      if (gGroup && settingsRef.current.gridLines) {
        const rect = renderer.domElement.getBoundingClientRect();
        mouseNDC.set(
          ((e.clientX - rect.left) / rect.width) * 2 - 1,
          -((e.clientY - rect.top) / rect.height) * 2 + 1,
        );
        raycaster.setFromCamera(mouseNDC, camera);
        const hitGuide = hitTestGuide(gGroup, raycaster);
        if (hitGuide) {
          e.preventDefault();
          let dragId = hitGuide.id;
          // Alt-drag = duplicate: clone the guide, drag the clone, leave
          // the original in place. Mirrors Figma / Sketch behaviour.
          if (e.altKey) {
            const src = settingsRef.current.guides?.find((g) => g.id === hitGuide.id);
            if (src) {
              const clone: Guide = {
                id: Math.random().toString(36).slice(2, 10),
                axis: src.axis,
                coord: src.coord,
              };
              pushSnapshot();
              setSettings((p) => ({ ...p, guides: [...(p.guides ?? []), clone] }));
              dragId = clone.id;
            }
          } else {
            pushSnapshot();
          }
          setSelectedGuideId(dragId);
          guideDragRef.current = { id: dragId, axis: hitGuide.axis, pointerId: e.pointerId };
          return;
        }
      }

      const hit = pickInstance(e.clientX, e.clientY);

      if (!hit && isLeftCameraShortcut && mode) {
        e.preventDefault();
        const orbitPivot = mode === "orbit" ? getSelectedPivot() : null;
        const startTarget = camTargetRef.current.clone();
        camDragRef.current = {
          mode,
          startX: e.clientX,
          startY: e.clientY,
          startYaw: camYawRef.current,
          startPitch: camPitchRef.current,
          startTarget,
          orbitPivot: orbitPivot ?? undefined,
          orbitTargetOffset: orbitPivot ? startTarget.clone().sub(orbitPivot) : undefined,
        };
        camDraggingRef.current = true;
        return;
      }

      // LINK MODE: clicking a target completes the link, anywhere else cancels.
      const linkingFrom = linkingFromIdRef.current;
      if (linkingFrom) {
        if (hit && hit.userData.id !== linkingFrom) {
          const targetId = hit.userData.id as string;
          pushSnapshot();
          setLinks((prev) => {
            const exists = prev.some(
              (l) =>
                (l.fromId === linkingFrom && l.toId === targetId) ||
                (l.fromId === targetId && l.toId === linkingFrom),
            );
            if (exists) return prev;
            return [...prev, { id: uid(), fromId: linkingFrom, toId: targetId }];
          });
        }
        setLinkingFromId(null);
        return;
      }

      if (hit) {
        const id = hit.userData.id as string;
        const obj = objectsRef.current.find((o) => o.id === id);
        if (!obj) return;
        if (e.altKey) {
          e.preventDefault();
          const cur = selectedIdsRef.current;
          const sourceIds = cur.includes(id) && cur.length > 1 ? cur : [id];
          const sourceSet = new Set(sourceIds);
          const duplicates: SceneObject[] = [];
          const newIds: string[] = [];
          let anchorDuplicate: SceneObject | null = null;
          for (const source of objectsRef.current) {
            if (!sourceSet.has(source.id)) continue;
            const duplicate: SceneObject = { ...source, id: uid() };
            duplicates.push(duplicate);
            newIds.push(duplicate.id);
            if (source.id === id) anchorDuplicate = duplicate;
          }
          if (duplicates.length === 0) return;
          anchorDuplicate ??= duplicates[0];
          const planePt = getPlaneAt(e.clientX, e.clientY);
          pushSnapshot();
          setObjects((prev) => [...prev, ...duplicates]);
          setSelectedIds(newIds);
          draggingRef.current = {
            anchorId: anchorDuplicate.id,
            offsetX: anchorDuplicate.x - (planePt?.x ?? 0),
            offsetZ: anchorDuplicate.z - (planePt?.z ?? 0),
            starts: new Map(duplicates.map((d) => [d.id, { x: d.x, z: d.z }])),
          };
          return;
        }
        // Decide the post-click selection set:
        //   - Shift-click: toggle this id in the existing selection (don't drag).
        //   - Plain click on already-selected: keep set, drag everything.
        //   - Plain click on un-selected: replace set with [id], drag it.
        const cur = selectedIdsRef.current;
        let nextIds: string[];
        if (e.shiftKey) {
          if (cur.includes(id)) nextIds = cur.filter((x) => x !== id);
          else nextIds = [...cur, id];
          setSelectedIds(nextIds);
          // No drag while toggling — feel matches most editors.
          return;
        } else if (cur.includes(id)) {
          nextIds = cur;
        } else {
          nextIds = [id];
          setSelectedIds(nextIds);
        }
        // Group drag: capture each member's start position; pointer plane-pos
        // gives the offset for the anchor only.
        const planePt = getPlaneAt(e.clientX, e.clientY);
        const starts = new Map<string, { x: number; z: number }>();
        for (const sid of nextIds) {
          const s = objectsRef.current.find((o) => o.id === sid);
          if (s) starts.set(sid, { x: s.x, z: s.z });
        }
        // Snapshot the pre-drag scene so a single Cmd+Z reverts the move.
        pushSnapshot();
        draggingRef.current = {
          anchorId: id,
          offsetX: obj.x - (planePt?.x ?? 0),
          offsetZ: obj.z - (planePt?.z ?? 0),
          starts,
        };
      } else {
        // Empty canvas: deselect (Shift preserves the set so users can
        // de-emphasise then click another object to add).
        if (!e.shiftKey) {
          setSelectedIds([]);
          setSelectedGuideId(null);
        }
      }
    }

    function onPointerMove(e: PointerEvent) {
      // Guide drag — slide along its only valid axis. Snap to 0.5 m unless
      // Shift is held (free drag). Updates settings.guides via setSettings,
      // so the rebuild-on-settings-change effect refreshes the geometry.
      const gDrag = guideDragRef.current;
      if (gDrag) {
        const rect = renderer.domElement.getBoundingClientRect();
        mouseNDC.set(
          ((e.clientX - rect.left) / rect.width) * 2 - 1,
          -((e.clientY - rect.top) / rect.height) * 2 + 1,
        );
        raycaster.setFromCamera(mouseNDC, camera);
        const snap = e.shiftKey ? 0 : 0.5;
        const next = dragCoordForRay(gDrag.axis, raycaster, snap);
        if (next === null) return;
        const half = settingsRef.current.groundSize / 2 - 0.5;
        const clamped = THREE.MathUtils.clamp(next, -half, half);
        setSettings((p) => ({
          ...p,
          guides: (p.guides ?? []).map((g) =>
            g.id === gDrag.id ? { ...g, coord: clamped } : g,
          ),
        }));
        return;
      }

      const drag = draggingRef.current;
      if (drag) {
        const planePt = getPlaneAt(e.clientX, e.clientY);
        if (!planePt) return;
        // Anchor's intended position from pointer.
        let ax = planePt.x + drag.offsetX;
        let az = planePt.z + drag.offsetZ;
        const anchorStart = drag.starts.get(drag.anchorId);
        if (!anchorStart) return;
        if (e.shiftKey) {
          // Axis-locked move based on the anchor.
          const dX = ax - anchorStart.x;
          const dZ = az - anchorStart.z;
          if (Math.abs(dX) > Math.abs(dZ)) az = anchorStart.z;
          else ax = anchorStart.x;
        }
        // Compute the world-space delta from the anchor's start; apply to
        // every member's start position to keep relative offsets locked.
        const dX = ax - anchorStart.x;
        const dZ = az - anchorStart.z;
        const half = settingsRef.current.groundSize / 2 - 0.5;
        setObjects((prev) =>
          prev.map((o) => {
            const start = drag.starts.get(o.id);
            if (!start) return o;
            return {
              ...o,
              x: THREE.MathUtils.clamp(start.x + dX, -half, half),
              z: THREE.MathUtils.clamp(start.z + dZ, -half, half),
            };
          }),
        );
        return;
      }

      const cd = camDragRef.current;
      if (cd) {
        const dx = e.clientX - cd.startX;
        const dy = e.clientY - cd.startY;
        if (cd.mode === "orbit") {
          // Turntable orbit: only yaw changes. Pitch stays fixed at whatever
          // the user last set via a numpad preset (or the iso default), so
          // the horizon never tilts during orbit drags.
          const deltaYaw = -dx * 0.0075;
          camYawRef.current = cd.startYaw + deltaYaw;
          if (cd.orbitPivot && cd.orbitTargetOffset) {
            const nextTarget = cd.orbitTargetOffset.clone().applyAxisAngle(worldUp, deltaYaw);
            camTargetRef.current.copy(cd.orbitPivot).add(nextTarget);
          }
        } else {
          panTarget(dx, dy, camDistanceRef.current);
        }
      }
    }

    function onPointerUp(e: PointerEvent) {
      (renderer.domElement as HTMLCanvasElement).releasePointerCapture?.(e.pointerId);
      if (guideDragRef.current && guideDragRef.current.pointerId === e.pointerId) {
        guideDragRef.current = null;
      }
      const cd = camDragRef.current;
      if (cd) {
        // Commit camera state back to settings so it persists with the profile.
        const t = camTargetRef.current;
        setSettings((prev) => ({
          ...prev,
          camera: {
            ...prev.camera,
            yaw: camYawRef.current,
            pitch: camPitchRef.current,
            targetX: t.x,
            targetY: t.y,
            targetZ: t.z,
          },
        }));
      }
      draggingRef.current = null;
      camDragRef.current = null;
      camDraggingRef.current = false;
    }

    // Suppress the browser context menu on the canvas so right-click can be
    // used for orbit (Blender-style fallback for users without a middle button).
    function onContextMenu(e: MouseEvent) {
      e.preventDefault();
    }

    // Double-click sets the orbit pivot. If the click hits an object, the
    // pivot snaps to that object's center. If it hits empty ground, the
    // pivot snaps to the ground point. Either way, subsequent orbit drags
    // revolve around the new target.
    function onDblClick(e: MouseEvent) {
      e.preventDefault();
      const hit = pickInstance(e.clientX, e.clientY);
      let targetWorld: THREE.Vector3 | null = null;
      if (hit) {
        const box = new THREE.Box3().setFromObject(hit);
        const c = new THREE.Vector3();
        box.getCenter(c);
        targetWorld = c;
      } else {
        targetWorld = getPlaneAt(e.clientX, e.clientY);
      }
      if (!targetWorld) return;
      camTargetRef.current.copy(targetWorld);
      pivotFlashUntilRef.current = performance.now() + 900;
      setSettings((prev) => ({
        ...prev,
        camera: {
          ...prev.camera,
          targetX: targetWorld!.x,
          targetY: targetWorld!.y,
          targetZ: targetWorld!.z,
        },
      }));
    }

    function onWheel(e: WheelEvent) {
      e.preventDefault();
      // Trackpad two-finger drag arrives as wheel events with ctrlKey when
      // pinching. We treat all wheel as dolly for simplicity, with a smaller
      // step for pinches.
      const isPinch = e.ctrlKey;
      const factor = Math.exp((isPinch ? e.deltaY * 2 : e.deltaY) * 0.001);
      camDistanceRef.current = THREE.MathUtils.clamp(
        camDistanceRef.current * factor,
        2,
        300,
      );
      // Persist after debounce-y commit on next frame.
      setSettings((prev) => ({
        ...prev,
        camera: { ...prev.camera, distance: camDistanceRef.current },
      }));
    }

    function onKey(e: KeyboardEvent) {
      // Avoid hijacking input fields
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;

      // Blender-style numpad views — Front (1) / Right (3) / Top (7) / 3-quarter (0).
      // Use e.code so layouts without a numpad still work via top-row digits if
      // numlock is off; e.key 1/3/7 also fires on the main row.
      let viewPreset: { yaw: number; pitch: number } | null = null;
      if (e.code === "Numpad1" || e.key === "1") viewPreset = { yaw: -Math.PI / 2, pitch: 0 };
      else if (e.code === "Numpad3" || e.key === "3") viewPreset = { yaw: 0, pitch: 0 };
      else if (e.code === "Numpad7" || e.key === "7") viewPreset = { yaw: -Math.PI / 2, pitch: Math.PI / 2 - 0.12 };
      else if (e.code === "Numpad0" || e.key === "0") viewPreset = { yaw: Math.PI / 4, pitch: Math.PI / 5 };
      if (viewPreset) {
        e.preventDefault();
        camYawRef.current = viewPreset.yaw;
        camPitchRef.current = viewPreset.pitch;
        setSettings((prev) => ({
          ...prev,
          camera: { ...prev.camera, yaw: viewPreset!.yaw, pitch: viewPreset!.pitch },
        }));
        return;
      }

      // Frame-selected (Numpad . or F): center camera target on the selected
      // object so subsequent orbit feels natural.
      if (e.code === "NumpadDecimal" || e.key === "f" || e.key === "F") {
        const sel = selectedIdRef.current;
        if (sel) {
          const pivot = getSelectedPivot();
          if (pivot) {
            const tx = pivot.x;
            const ty = pivot.y;
            const tz = pivot.z;
            camTargetRef.current.set(tx, ty, tz);
            pivotFlashUntilRef.current = performance.now() + 900;
            setSettings((prev) => ({
              ...prev,
              camera: { ...prev.camera, targetX: tx, targetY: ty, targetZ: tz },
            }));
          }
        }
        e.preventDefault();
        return;
      }

      // Cmd/Ctrl+Z and Cmd/Ctrl+Shift+Z: undo / redo. Handled outside the
      // selection-required block so they fire even with no selection.
      const isUndoCombo = (e.metaKey || e.ctrlKey) && (e.key === "z" || e.key === "Z");
      if (isUndoCombo) {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
        return;
      }

      // Escape cancels link-in-progress. Brings the SelectionCard back
      // (it gets dimmed while linkingFromId is set).
      if (e.key === "Escape" && linkingFromIdRef.current) {
        linkingFromIdRef.current = null;
        setLinkingFromId(null);
        e.preventDefault();
        return;
      }

      // Backspace/Delete also deletes the selected guide (independent of any
      // selected scene objects — handled before the object-selection guard).
      if (selectedGuideIdRef.current && (e.key === "Backspace" || e.key === "Delete")) {
        const gid = selectedGuideIdRef.current;
        pushSnapshot();
        setSettings((p) => ({ ...p, guides: (p.guides ?? []).filter((g) => g.id !== gid) }));
        setSelectedGuideId(null);
        e.preventDefault();
        return;
      }

      const ids = selectedIdsRef.current;
      if (ids.length === 0) return;
      const idSet = new Set(ids);
      if (e.key === "Backspace" || e.key === "Delete") {
        pushSnapshot();
        setObjects((prev) => prev.filter((o) => !idSet.has(o.id)));
        setLinks((prev) => prev.filter((l) => !idSet.has(l.fromId) && !idSet.has(l.toId)));
        setSelectedIds([]);
        e.preventDefault();
      } else if (e.key === "r" || e.key === "R") {
        pushSnapshot();
        setObjects((prev) =>
          prev.map((o) =>
            idSet.has(o.id) ? { ...o, rotation: o.rotation + Math.PI / 2 } : o,
          ),
        );
      } else if (e.key === "d" || e.key === "D") {
        pushSnapshot();
        const newIds: string[] = [];
        setObjects((prev) => {
          const dups: SceneObject[] = [];
          for (const o of prev) {
            if (!idSet.has(o.id)) continue;
            const nid = uid();
            newIds.push(nid);
            dups.push({ ...o, id: nid, x: o.x + 2, z: o.z });
          }
          return [...prev, ...dups];
        });
        if (newIds.length) setSelectedIds(newIds);
      }
    }

    const dom = renderer.domElement;
    dom.addEventListener("pointerdown", onPointerDown);
    dom.addEventListener("pointermove", onPointerMove);
    dom.addEventListener("pointerup", onPointerUp);
    dom.addEventListener("pointercancel", onPointerUp);
    dom.addEventListener("wheel", onWheel, { passive: false });
    dom.addEventListener("contextmenu", onContextMenu);
    dom.addEventListener("dblclick", onDblClick);
    window.addEventListener("keydown", onKey);

    // ---------- resize ----------
    function onResize() {
      const w = mount.clientWidth;
      const h = mount.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    onResize();
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    // ---------- render loop ----------
    let raf = 0;
    function tick() {
      const now = performance.now();
      const s = settingsRef.current;

      // Selection does not move the camera. The orbit target only changes via
      // explicit camera actions: selected-object orbit, pan, double-click
      // pivot, frame-selected, add, reset, or sidebar controls.

      // Blender-style orbit camera. Position is computed each frame from
      // (target, yaw, pitch, distance). Pitch is hard-clamped per-frame so
      // legacy-saved profiles with near-vertical pitch (where lookAt becomes
      // degenerate against world-up) are repaired on first paint.
      const t = camTargetRef.current;
      const yaw = camYawRef.current;
      const pitch = THREE.MathUtils.clamp(camPitchRef.current, CAMERA_PITCH_MIN, CAMERA_PITCH_MAX);
      camPitchRef.current = pitch;
      const d = camDistanceRef.current;
      const cosP = Math.cos(pitch);
      const sinP = Math.sin(pitch);
      camera.position.set(
        t.x + d * cosP * Math.cos(yaw),
        t.y + d * sinP,
        t.z + d * cosP * Math.sin(yaw),
      );
      camera.up.set(0, 1, 0);
      camera.lookAt(t);

      if (camera.fov !== s.camera.fov) {
        camera.fov = s.camera.fov;
        camera.updateProjectionMatrix();
      }

      // Pivot indicator: anchored to the user's last shift+RMB pivot point
      // (or the orbit pivot during selected-object orbit). Stays put when
      // panning — pan moves camTargetRef, not the visual pivot.
      const piv = pivotRef.current;
      if (piv) {
        const visiblePivot = camDragRef.current?.mode === "orbit" && camDragRef.current.orbitPivot
          ? camDragRef.current.orbitPivot
          : (pivotPointRef.current ?? t);
        piv.position.copy(visiblePivot);
        const sc = Math.max(0.4, d * 0.025);
        piv.scale.setScalar(sc);
        // Visible only while actively orbiting/panning, and briefly after a
        // shift+RMB pivot set (the flash window). Hides at all other times so
        // the dot doesn't sit on screen during normal navigation.
        const wantOpacity = camDraggingRef.current || now < pivotFlashUntilRef.current ? 1 : 0;
        // Smooth fade
        const cur = (piv.userData.opacity as number | undefined) ?? 0;
        const next = cur + (wantOpacity - cur) * 0.18;
        piv.userData.opacity = next;
        piv.visible = next > 0.02;
        piv.traverse((o) => {
          const m = (o as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined;
          if (!m) return;
          // Only the pivot's own materials, not anything that might have
          // accidentally been parented (none currently).
          (m as THREE.MeshBasicMaterial).opacity = next * (m.userData.baseOpacity ?? 0.95);
        });
      }

      // Animate links
      if (linksGroupRef.current && s.animateLinks) {
        tickLinks(linksGroupRef.current, now * 0.001, s.linkSpeed);
      }

      // Subtle per-object emissive pulse + occasional flicker. Runs whether
      // links animate or not — the lights are part of the object's own life,
      // not the data flow.
      tickBlink(now * 0.001, 0.35);

      // Per-frame selection outlines. Keeping this in the render loop (vs a
      // useEffect on selectedIds) means a freshly added object whose GLB has
      // just finished loading gets its outline the same frame it mounts.
      {
        const ids = selectedIdsRef.current;
        const wantSet = new Set(ids);
        const prev = selectionOutlinesRef.current;
        // Compute desired world bboxes for each selected id (skip ids whose
        // instance hasn't mounted yet — they'll get an outline on a later
        // frame).
        const wanted = new Map<string, THREE.Box3>();
        if (worldRef.current) {
          for (const child of worldRef.current.children) {
            const id = child.userData?.id as string | undefined;
            if (!id || !wantSet.has(id)) continue;
            wanted.set(id, new THREE.Box3().setFromObject(child));
          }
        }
        // Dispose helpers whose id is no longer wanted.
        const keep: THREE.Box3Helper[] = [];
        for (const h of prev) {
          const hid = h.userData?.boxId as string | undefined;
          if (hid && wanted.has(hid)) keep.push(h);
          else if (sceneRef.current) sceneRef.current.remove(h);
        }
        selectionOutlinesRef.current = keep;
        // Update or create helpers for each wanted id.
        for (const [id, box] of wanted) {
          let helper = keep.find((h) => h.userData?.boxId === id);
          if (!helper) {
            helper = new THREE.Box3Helper(box, new THREE.Color(0x4a8eff));
            helper.userData.boxId = id;
            selectionOutlinesRef.current.push(helper);
            scene.add(helper);
          } else {
            (helper.box as THREE.Box3).copy(box);
            helper.updateMatrixWorld(true);
          }
        }
      }

      // Selection label projection. Compute the screen-space coords of the
      // top of the selected object's bbox and store on a ref; only set state
      // when it changes meaningfully so we don't re-render every frame.
      if (worldRef.current) {
        const sel = selectedIdRef.current;
        let next: { x: number; y: number } | null = null;
        if (sel) {
          const obj = worldRef.current.children.find((c) => c.userData?.id === sel);
          if (obj) {
            const box = new THREE.Box3().setFromObject(obj);
            const top = new THREE.Vector3(
              (box.min.x + box.max.x) / 2,
              box.max.y + 0.4,
              (box.min.z + box.max.z) / 2,
            );
            const ndc = top.clone().project(camera);
            if (ndc.z > -1 && ndc.z < 1 && ndc.x > -1.05 && ndc.x < 1.05) {
              const rect = renderer.domElement.getBoundingClientRect();
              next = {
                x: ((ndc.x + 1) / 2) * rect.width,
                y: ((1 - ndc.y) / 2) * rect.height,
              };
            }
          }
        }
        const cur = labelPosRef.current;
        const changed =
          (cur === null) !== (next === null) ||
          (cur && next && (Math.abs(cur.x - next.x) > 0.5 || Math.abs(cur.y - next.y) > 0.5));
        if (changed) {
          labelPosRef.current = next;
          setLabelPos(next);
        }
        // Drive the floating SelectionCard via the same projected point so
        // it follows the selected object frame-by-frame without React state
        // churn. Hide while dragging so it doesn't sit on top of the object.
        if (selectionCardRef.current) {
          const dragging = draggingRef.current !== null || camDraggingRef.current;
          selectionCardRef.current.setAnchor(dragging ? null : next);
        }
      }

      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    }
    tick();

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      dom.removeEventListener("pointerdown", onPointerDown);
      dom.removeEventListener("pointermove", onPointerMove);
      dom.removeEventListener("pointerup", onPointerUp);
      dom.removeEventListener("pointercancel", onPointerUp);
      dom.removeEventListener("wheel", onWheel);
      dom.removeEventListener("contextmenu", onContextMenu);
      dom.removeEventListener("dblclick", onDblClick);
      window.removeEventListener("keydown", onKey);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
    // We intentionally only re-run on manifest change. Other settings are
    // read live from `settingsRef`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifest]);

  // ---------- sync world group with `objects` state ----------
  useEffect(() => {
    const world = worldRef.current;
    if (!world) return;
    let cancelled = false;

    (async () => {
      const existing = new Map<string, THREE.Object3D>();
      world.children.forEach((c) => {
        if (c.userData?.id) existing.set(c.userData.id, c);
      });

      // Track whether we mounted any new instances this pass. The link
      // rebuild depends on bboxes from those instances, so we bump
      // `instancesVersion` at the end of an "added something" pass so
      // it re-runs against the now-existing children.
      let addedAny = false;

      // Remove gone
      const wantedIds = new Set(objects.map((o) => o.id));
      for (const [id, child] of existing) {
        if (!wantedIds.has(id)) {
          world.remove(child);
          if (child.userData?.generatedText) disposeGeneratedObject(child);
          else if (child.userData?.generatedRow) disposeRackRowObject(child);
          (child as THREE.Mesh).geometry?.dispose?.();
        }
      }

      // Add or update
      for (const o of objects) {
        let inst = existing.get(o.id);
        const accentHex = new THREE.Color(resolveAccent(settingsRef.current)).getHex();
        if (!inst) {
          inst = isTextLabelKind(o.kind)
            ? createTextLabelObject(o, accentHex)
            : isRackRowKind(o.kind)
              ? await createRackRowObject(o)
              : await getInstanceOrPlaceholder(o.kind);
          if (cancelled) return;
          inst.userData.id = o.id;
          inst.userData.kind = o.kind;
          if (isTextLabelKind(o.kind)) inst.userData.textObjectTag = textObjectTag(o, accentHex);
          if (isRackRowKind(o.kind)) inst.userData.rackRowTag = rackRowTag(o);
          inst.traverse((c) => {
            if ((c as THREE.Mesh).isMesh) {
              c.castShadow = true;
              c.receiveShadow = true;
            }
          });
          if (settingsRef.current.floatEnabled) {
            attachFloatShadow(inst, settingsRef.current.floatShadowSize, settingsRef.current.floatShadowOpacity);
          }
          // Register emissive sub-meshes for the per-object blink animation.
          // Same per-instance clone strategy used in the iso view.
          registerBlinkInstance(inst, o.id);
          world.add(inst);
          addedAny = true;
        } else if (isTextLabelKind(o.kind)) {
          const nextTextTag = textObjectTag(o, accentHex);
          if (inst.userData.textObjectTag !== nextTextTag) {
            const next = createTextLabelObject(o, accentHex);
            next.userData.id = o.id;
            next.userData.kind = o.kind;
            next.userData.textObjectTag = nextTextTag;
            world.remove(inst);
            if (inst.userData.generatedText) disposeGeneratedObject(inst);
            world.add(next);
            inst = next;
            existing.set(o.id, next);
          }
        } else if (isRackRowKind(o.kind)) {
          const nextRowTag = rackRowTag(o);
          if (inst.userData.rackRowTag !== nextRowTag) {
            const next = await createRackRowObject(o);
            if (cancelled) return;
            next.userData.id = o.id;
            next.userData.kind = o.kind;
            next.userData.rackRowTag = nextRowTag;
            world.remove(inst);
            if (inst.userData.generatedRow) disposeRackRowObject(inst);
            world.add(next);
            inst = next;
            existing.set(o.id, next);
          }
        }
        const baseY = settingsRef.current.floatEnabled ? settingsRef.current.floatHeight : 0;
        if (isBrandCanvasKind(o.kind)) {
          applyBrandCanvasTransform(inst, o, baseY);
          const materialMode = resolveBrandCanvasMaterialMode(o);
          const appearanceTag = materialMode === "accent" ? `accent:${accentHex.toString(16)}` : materialMode;
          if (inst.userData.brandCanvasAppearanceTag !== appearanceTag) {
            inst.userData.brandCanvasAppearanceTag = appearanceTag;
            applyBrandCanvasAppearance(inst, materialMode, accentHex);
          }
        } else {
          inst.position.set(o.x, baseY, o.z);
          inst.rotation.set(0, o.rotation, 0);
          inst.scale.setScalar(o.scale);
          // Accent override (blue brand) on the first material slot of the
          // first mesh, so the rest of the materials remain unchanged.
          // Toggling off restores the original material via the stash on
          // the mesh's userData.
          if (o.accent) applyAccentOverride(inst, accentHex);
          else clearAccentOverride(inst);
        }
        // Brand-mark decal: only re-apply if placement options changed from
        // the prior render so we don't re-load on every drag.
        const oThickness = o.brandThickness ?? 0.15;
        const brandMarkMaterialMode = resolveBrandMarkMaterialMode(o);
        const tag = [
          o.brandMark ?? "none",
          o.brandSide ?? "none",
          oThickness.toFixed(2),
          o.brandFlip ? "flip" : "normal",
          o.brandInvert ? "inv" : "noinv",
          brandMarkMaterialMode === "accent" ? `accent:${accentHex.toString(16)}` : brandMarkMaterialMode,
        ].join(":");
        if (inst.userData.brandMarkTag !== tag) {
          inst.userData.brandMarkTag = tag;
          if (o.brandMark && o.brandSide) {
            void applyBrandMark(inst, o.brandMark, o.brandSide, oThickness, !!o.brandFlip, !!o.brandInvert, brandMarkMaterialMode, accentHex);
          } else {
            clearBrandMark(inst);
          }
        }
      }
      // If we mounted any new instances this pass, kick the link
      // rebuild so it can read their world bboxes (which would have
      // returned empty if the rebuild ran before the GLB clones landed).
      if (addedAny && !cancelled) {
        setInstancesVersion((v) => v + 1);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [objects, manifest]);

  // Selection outlines are managed per-frame in the render loop instead of
  // a useEffect — that way they appear the frame the GLB instance actually
  // mounts (the world-sync useEffect is async due to loadGLB), so a freshly
  // added object via "Add" gets its outline immediately. They also track
  // drags without needing a re-render.

  // ---------- rebuild links when links/objects change ----------
  useEffect(() => {
    const lg = linksGroupRef.current;
    const world = worldRef.current;
    if (!lg || !world) return;
    const objectsById = new Map(objects.map((o) => [o.id, o]));
    rebuildLinks(lg, links, {
      objectsById,
      worldGroup: world,
      thickness: settings.linkThickness,
      color: new THREE.Color(resolveLinkColor(settings)),
      straight: settings.straightLinks,
      cableOpacity: settings.linkCableOpacity,
      stripOpacity: settings.linkStripOpacity,
      power: settings.linkPower,
    });
  }, [
    links,
    objects,
    settings.linkThickness,
    settings.linkColorDark,
    settings.linkColorLight,
    settings.straightLinks,
    settings.theme,
    settings.linkCableOpacity,
    settings.linkStripOpacity,
    settings.linkPower,
    // Re-fire after the world-sync effect actually mounts new instances,
    // so the link rebuild reads non-empty bboxes on first load.
    instancesVersion,
  ]);

  // ---------- theme application ----------
  useEffect(() => {
    const scene = sceneRef.current;
    const renderer = rendererRef.current;
    const ground = groundRef.current;
    const dot = dotGridRef.current;
    const lights = lightsRef.current;
    if (!scene || !renderer || !ground || !dot || !lights) return;
    const dotMat = dot.material as THREE.MeshBasicMaterial;
    applyTheme(settings.theme, scene, renderer, ground, dotMat, lights.hemi, lights.key, lights.fill, lights.rim, lights.floorGlow);
    applyDotGridOpacity(dotMat, settings);
    dotMat.visible = settings.dotGrid;
    // Rebuild dot texture if spacing/color changed
    if (dotMat.map) dotMat.map.dispose();
    dotMat.map = buildDotGridTexture(settings.groundSize, settings.dotGridSpacing, resolveDotColor(settings), settings.dotGridSize);
    dotMat.needsUpdate = true;
    // applyTheme can rewrite environmentIntensity / exposure as part of its
    // palette swap; re-apply the active lighting version so V2 takes
    // precedence over the theme defaults.
    applyLightingVersion(renderer, scene, lightingVersion, baseEnvIntensityRef.current);
    applyLightingV4Extras(scene, renderer, lights, dotMat, ground, lightingVersion, THEMES[settings.theme]);
  }, [settings.theme, settings.dotGrid, settings.dotGridSpacing, settings.dotGridSize, settings.dotGridOpacityDark, settings.dotGridOpacityLight, settings.dotColorDark, settings.dotColorLight, settings.groundSize, lightingVersion]);

  // Guides overlay rebuild — runs whenever the guide list, any look
  // setting, or selection changes. Cheap (a few LineSegments). Every
  // gridLines* field must be in the dep list so panel edits propagate.
  useEffect(() => {
    const g = guidesRef.current;
    if (!g) return;
    rebuildGuides(g, settings, selectedGuideId);
  }, [
    settings.gridLines,
    settings.gridLinesShowCrosses,
    settings.gridLinesCrossSize,
    settings.gridLinesOpacityDark,
    settings.gridLinesOpacityLight,
    settings.gridLinesCrossOpacityDark,
    settings.gridLinesCrossOpacityLight,
    settings.gridLinesColorDark,
    settings.gridLinesColorLight,
    settings.gridLinesDashSize,
    settings.gridLinesGapSize,
    settings.guides,
    settings.theme,
    settings.groundSize,
    selectedGuideId,
  ]);

  // Lighting version effect — covers the case where the user toggles V1/V2
  // without otherwise touching theme settings.
  useEffect(() => {
    const scene = sceneRef.current;
    const renderer = rendererRef.current;
    if (!scene || !renderer) return;
    applyLightingVersion(renderer, scene, lightingVersion, baseEnvIntensityRef.current);
    const ground = groundRef.current;
    const dot = dotGridRef.current;
    const lights = lightsRef.current;
    if (ground && dot && lights) {
      const dotMat = dot.material as THREE.MeshBasicMaterial;
      applyLightingV4Extras(scene, renderer, lights, dotMat, ground, lightingVersion, THEMES[settings.theme]);
    }
  }, [lightingVersion]);

  // ============================================================
  // Object actions
  // ============================================================
  const addKind = useCallback((kind: SceneKind) => {
    // Drop the new object next to the currently selected one so the user
    // doesn't have to drag it into place every time. Step +X in 2 m
    // increments from the selection until a clear slot opens up (avoids
    // stacking on existing neighbours when adding multiple in a row).
    const all = objectsRef.current;
    const selId = selectedIdsRef.current[0];
    const sel = selId ? all.find((p) => p.id === selId) : undefined;
    let nx = 0;
    let nz = 0;
    if (sel) {
      const STEP = 2;
      const PROXIMITY = 1.2;
      nx = sel.x + STEP;
      nz = sel.z;
      const clash = (x: number, z: number) =>
        all.some((p) => p.id !== sel.id && Math.hypot(p.x - x, p.z - z) < PROXIMITY);
      let guard = 32;
      while (clash(nx, nz) && guard-- > 0) nx += STEP;
    }
    const o: SceneObject = {
      id: uid(),
      kind,
      x: nx,
      z: nz,
      scale: 1,
      rotation: 0,
      ...defaultBrandObjectPatch(kind),
      ...(isRackRowKind(kind) ? defaultRackRowPatch() : {}),
    };
    pushSnapshot();
    setObjects((prev) => [...prev, o]);
    setSelectedId(o.id);
  }, [pushSnapshot]);

  const updateSelected = useCallback((patch: Partial<SceneObject>) => {
    const ids = selectedIdsRef.current;
    if (ids.length === 0) return;
    pushSnapshot();
    const set = new Set(ids);
    setObjects((prev) =>
      prev.map((o) => (set.has(o.id) ? { ...o, ...patch } : o)),
    );
  }, [pushSnapshot]);

  const removeSelected = useCallback(() => {
    const ids = selectedIdsRef.current;
    if (ids.length === 0) return;
    pushSnapshot();
    const set = new Set(ids);
    setObjects((prev) => prev.filter((o) => !set.has(o.id)));
    setLinks((prev) => prev.filter((l) => !set.has(l.fromId) && !set.has(l.toId)));
    setSelectedIds([]);
  }, [pushSnapshot]);

  /**
   * Run an alignment/distribute op (one of the 8 in lib/align.ts) on the
   * currently selected objects. Reads each instance's live world bbox
   * from the scene graph so the math accounts for the rendered footprint
   * (the same approach the iso off-floor guard uses).
   *
   * Pushes one snapshot before the change so a single Cmd+Z reverts it.
   */
  const runAlignOp = useCallback((
    op: (objects: SceneObject[], bboxes: Map<string, THREE.Box3>) => AlignmentResult,
  ) => {
    const ids = selectedIdsRef.current;
    if (ids.length < 2) return;
    const world = worldRef.current;
    if (!world) return;
    const idSet = new Set(ids);
    const selectedObjects = objectsRef.current.filter((o) => idSet.has(o.id));
    // Map id → world bbox for every selected instance present in the scene.
    const bboxes = new Map<string, THREE.Box3>();
    for (const child of world.children) {
      const id = child.userData?.id as string | undefined;
      if (!id || !idSet.has(id)) continue;
      bboxes.set(id, new THREE.Box3().setFromObject(child));
    }
    const patches = op(selectedObjects, bboxes);
    if (patches.size === 0) return;
    pushSnapshot();
    setObjects((prev) => applyPatches(prev, patches));
  }, [pushSnapshot]);

  const startLinkFromSelected = useCallback(() => {
    if (!selectedId) return;
    setLinkingFromId(selectedId);
  }, [selectedId]);

  const addLinkFromSelectedTo = useCallback((toId: string) => {
    if (!selectedId) return;
    pushSnapshot();
    setLinks((prev) => {
      // Don't dupe.
      if (prev.some((k) => (k.fromId === selectedId && k.toId === toId) || (k.fromId === toId && k.toId === selectedId))) {
        return prev;
      }
      return [...prev, { id: uid(), fromId: selectedId, toId }];
    });
  }, [selectedId, pushSnapshot]);

  const clearLinksFromSelected = useCallback(() => {
    if (!selectedId) return;
    pushSnapshot();
    setLinks((prev) => prev.filter((k) => k.fromId !== selectedId && k.toId !== selectedId));
  }, [selectedId, pushSnapshot]);

  const disconnectSceneLink = useCallback((linkId: string) => {
    pushSnapshot();
    setLinks((prev) => prev.filter((k) => k.id !== linkId));
  }, [pushSnapshot]);

  const exportPng = useCallback(() => {
    const r = rendererRef.current;
    const s = sceneRef.current;
    const c = cameraRef.current;
    if (!r || !s || !c) return;
    saveImage(r, s, c, { includeBackground: true }, `oneqode-${Date.now()}.png`);
  }, []);

  const exportPngTransparent = useCallback(() => {
    const r = rendererRef.current;
    const s = sceneRef.current;
    const c = cameraRef.current;
    if (!r || !s || !c) return;
    saveImage(r, s, c, { includeBackground: false }, `oneqode-${Date.now()}-transparent.png`);
  }, []);

  const copyPng = useCallback(async (includeBackground: boolean) => {
    const r = rendererRef.current;
    const s = sceneRef.current;
    const c = cameraRef.current;
    if (!r || !s || !c) return;
    try {
      await copyToClipboard(r, s, c, { includeBackground });
      setToast({
        id: Date.now(),
        message: includeBackground ? "Copied PNG" : "Copied transparent PNG",
      });
    } catch (e) {
      alert((e as Error).message);
    }
  }, []);

  // Snap to a canonical view. Keeps target + distance, only sets yaw/pitch.
  type ViewName =
    | "front" | "back" | "right" | "left" | "top" | "bottom"
    | "iso_ne" | "iso_nw" | "iso_sw" | "iso_se"
    | "iso";
  const setView = useCallback((name: ViewName) => {
    const v = CAMERA_VIEWS[name];
    setSettings((p) => ({
      ...p,
      camera: {
        ...p.camera,
        yaw: v.yaw ?? p.camera.yaw,
        pitch: v.pitch ?? p.camera.pitch,
      },
    }));
  }, []);

  /**
   * Rotate the current view by `deltaYaw` radians around the orbit pivot,
   * preserving pitch and distance. Used for the ±90° / 180° quick-rotate
   * buttons so the user can fix "I'm looking from the top but it's
   * upside down" without hunting for the right axial preset.
   *
   * Yaw is normalised to [-π, π] after the rotation so values don't drift
   * out of range over many clicks.
   */
  const rotateView = useCallback((deltaYaw: number) => {
    setSettings((p) => {
      let yaw = p.camera.yaw + deltaYaw;
      // Wrap to [-π, π].
      yaw = Math.atan2(Math.sin(yaw), Math.cos(yaw));
      return { ...p, camera: { ...p.camera, yaw } };
    });
  }, []);

  // Move the orbit pivot back to the world origin — middle of the scene floor.
  const recenterPivot = useCallback(() => {
    camTargetRef.current.set(0, 0, 0);
    pivotFlashUntilRef.current = performance.now() + 900;
    setSettings((p) => ({
      ...p,
      camera: { ...p.camera, targetX: 0, targetY: 0, targetZ: 0 },
    }));
  }, []);

  // Reset camera to the iso preset and re-frame on the world origin.
  const resetCamera = useCallback(() => {
    setSettings((p) => ({
      ...p,
      camera: {
        ...DEFAULT_SETTINGS.camera,
      },
    }));
  }, []);

  // ---------- profile actions ----------
  const switchProfile = useCallback((ref: ProfileRef) => {
    void autoSaverRef.current.flush();
    setActiveRef(ref);
  }, []);

  // Create a new profile. Always routes by host: localhost → server file,
  // visitor → localStorage. No mode toggle in the UI; the destination is
  // implicit and matches what subsequent autosaves will do.
  const newProfile = useCallback(() => {
    const defaultName = "Untitled scene";
    const name = window.prompt(
      editor
        ? "Name for new server profile (committed to repo):"
        : "Name for new local profile (saved in your browser):",
      defaultName,
    );
    if (!name) return;
    // Start empty: new profile inherits the active settings (camera,
    // theme, dot grid, etc.) but has zero objects and no links so the
    // user gets a blank canvas to compose into. Duplicate-current would
    // be a separate command if needed later.
    const buildPayload = (slug: string) => ({
      version: 2,
      kind: "scene-profile" as const,
      profile: {
        id: slug, name, settings, objects: [], links: [], updatedAt: Date.now(),
      },
    });
    if (editor) {
      const slug = slugFromName("scene", name);
      if (slug === "scene-main") { alert("Reserved slug. Pick a different name."); return; }
      void (async () => {
        await fetch(`/__save-profile/${slug}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildPayload(slug)),
        });
        const all = await listServerProfiles();
        setServerList(all.filter((e) => e.kind === "scene"));
        setActiveRef(makeServerRef("scene", slug, name));
      })();
    } else {
      const slug = newLocalSlug("scene");
      saveLocalProfile("scene", slug, name, buildPayload(slug));
      setLocalList(listLocalProfiles("scene"));
      setActiveRef(makeLocalRef("scene", slug, name));
    }
  }, [editor, settings]);

  /**
   * Save the *current* scene as a new local profile. This is what the
   * "+ Duplicate to local" button is supposed to do — keep the user's
   * in-progress edits when they're viewing a read-only server profile
   * off-localhost. Different from `newProfile`, which always starts
   * empty.
   */
  const duplicateToLocal = useCallback(() => {
    const defaultName = `${activeRef.name} (copy)`;
    const name = window.prompt(
      "Name for the local copy (saved in your browser):",
      defaultName,
    );
    if (!name) return;
    const slug = newLocalSlug("scene");
    const payload = {
      version: 2,
      kind: "scene-profile" as const,
      profile: {
        id: slug, name, settings,
        objects: objectsRef.current,
        links: linksRef.current,
        updatedAt: Date.now(),
      },
    };
    saveLocalProfile("scene", slug, name, payload);
    setLocalList(listLocalProfiles("scene"));
    setActiveRef(makeLocalRef("scene", slug, name));
  }, [activeRef.name, settings]);

  const removeProfile = useCallback(async (ref: ProfileRef) => {
    if (ref.source === "server") {
      if (ref.slug === "scene-main") return;
      if (!editor) return;
      if (!confirm(`Delete server profile "${ref.name}"? This removes the file on disk.`)) return;
      await deleteServerProfile(ref.slug);
      const all = await listServerProfiles();
      setServerList(all.filter((e) => e.kind === "scene"));
    } else {
      if (!confirm(`Delete local profile "${ref.name}"?`)) return;
      deleteLocalProfile("scene", ref.slug);
      setLocalList(listLocalProfiles("scene"));
    }
    if (activeRef.source === ref.source && activeRef.slug === ref.slug) {
      setActiveRef(defaultMainRef("scene"));
    }
  }, [editor, activeRef]);

  const renameLocal = useCallback((slug: string) => {
    const cur = listLocalProfiles("scene").find((p) => p.slug === slug);
    if (!cur) return;
    const name = window.prompt("Rename local profile:", cur.name);
    if (!name) return;
    renameLocalProfile("scene", slug, name);
    setLocalList(listLocalProfiles("scene"));
    if (activeRef.source === "local" && activeRef.slug === slug) {
      setActiveRef({ ...activeRef, name });
    }
  }, [activeRef]);

  const exportActiveAsJson = useCallback(() => {
    const payload = {
      version: 2,
      kind: "scene-profile",
      profile: {
        id: activeRef.slug,
        name: activeRef.name,
        settings,
        objects,
        links,
        updatedAt: Date.now(),
      },
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeRef.slug}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }, [activeRef, settings, objects, links]);

  const importJsonFile = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const payload = parsed?.profile ?? parsed;
      if (!payload?.settings) throw new Error("Missing settings");
      const slug = newLocalSlug("scene");
      const name = payload.name ? `${payload.name} (imported)` : "Imported scene";
      saveLocalProfile("scene", slug, name, {
        version: 2,
        kind: "scene-profile",
        profile: { ...payload, id: slug, name },
      });
      setLocalList(listLocalProfiles("scene"));
      setActiveRef(makeLocalRef("scene", slug, name));
    } catch (e) {
      alert(`Import failed: ${(e as Error).message}`);
    }
  }, []);

  // ============================================================
  // UI
  // ============================================================
  const selected = useMemo(
    () => (selectedId ? objects.find((o) => o.id === selectedId) ?? null : null),
    [objects, selectedId],
  );

  if (error) return <ErrorScreen msg={error} />;
  if (!manifest) return <Loading />;

  const groups = groupCatalog(manifest.catalog);

  return (
    <div style={styles.root}>
      <div ref={mountRef} style={styles.canvasMount} />

      {/* Top status overlay */}
      <div style={styles.topBar}>
        <Branding />
        {!editor && activeRef.source === "server" && (
          <span style={styles.readOnlyChip} title="Server profiles are read-only outside localhost — switch to a local profile to edit">
            Read-only
          </span>
        )}
        <div style={styles.spacer} />
        <button style={styles.btn} onClick={() => exportPng()}>Export PNG</button>
        <button style={styles.btn} onClick={() => exportPngTransparent()}>Export Transparent PNG</button>
        <button style={styles.btn} onClick={() => void copyPng(true)}>Copy PNG</button>
        <button style={styles.btn} onClick={() => void copyPng(false)}>Copy Transparent PNG</button>
        <button style={styles.helpBtn} onClick={() => setHelpOpen(true)} title="Show keyboard & nav help (?)">
          ?
        </button>
      </div>
      <Toast toast={toast} />

      {/* Linking-mode banner */}
      {linkingFromId && (
        <div style={styles.linkingBanner}>
          Linking — click another object to connect, or press Esc to cancel.
        </div>
      )}

      {/* Floating SelectionCard — follows the selected object in screen space. */}
      {selectedId && selected && selectedIds.length === 1 && (() => {
        const peerLabel = (id: string) => objects.find((o) => o.id === id)?.kind ?? "?";
        const connections = links
          .filter((k) => k.fromId === selected.id || k.toId === selected.id)
          .map((k) => ({
            id: k.id,
            peerLabel: peerLabel(k.fromId === selected.id ? k.toId : k.fromId),
          }));
        return (
          <SelectionCard
            ref={selectionCardRef}
            kind={selected.kind}
            rotation={selected.rotation}
            scale={selected.scale}
            accent={!!selected.accent}
            brandColor={isBrandCanvasKind(selected.kind)
              ? resolveBrandCanvasMaterialMode(selected)
              : resolveBrandMarkMaterialMode(selected)}
            brandMark={selected.brandMark ?? null}
            brandSide={selected.brandSide ?? null}
            brandThickness={selected.brandThickness ?? (isBrandCanvasKind(selected.kind) ? 1 : 0.15)}
            brandFlip={!!selected.brandFlip}
            brandInvert={!!selected.brandInvert}
            isBrandCanvasObject={isBrandCanvasKind(selected.kind)}
            isTextObject={isTextLabelKind(selected.kind)}
            brandPose={selected.brandPose ?? "upright"}
            textContent={selected.textContent ?? "OneQode"}
            textFont={selected.textFont ?? "sans"}
            textDepth={selected.textDepth ?? 0.06}
            isRackRowObject={isRackRowKind(selected.kind)}
            rowPreset={selected.rowPreset ?? "medium"}
            rowCount={selected.rowCount ?? 8}
            rowPair={!!selected.rowPair}
            rowAisleWidth={selected.rowAisleWidth ?? 2.4}
            linkingMode={linkingFromId === selected.id}
            connections={connections}
            onChange={(patch) => updateSelected(patch)}
            onStartLink={() => setLinkingFromId(selected.id)}
            onCancelLink={() => setLinkingFromId(null)}
            onDisconnect={disconnectSceneLink}
            onClearLinks={clearLinksFromSelected}
            onDelete={removeSelected}
          />
        );
      })()}

      {helpOpen && <HelpDialog onClose={() => setHelpOpen(false)} />}

      {/* Left sidebar — profile + catalog */}
      <aside style={styles.leftPanel}>
        <Section title="Profiles">
          <ProfilePicker
            viewKind="scene"
            activeRef={activeRef}
            serverList={serverList}
            localList={localList}
            editor={editor}
            saveStatus={saveStatus}
            onSwitch={switchProfile}
            onNewProfile={newProfile}
            onDuplicateToLocal={duplicateToLocal}
            onDelete={removeProfile}
            onRenameLocal={renameLocal}
            onExport={exportActiveAsJson}
            onImportClick={() => fileInputRef.current?.click()}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void importJsonFile(f);
              if (fileInputRef.current) fileInputRef.current.value = "";
            }}
          />
        </Section>

        <Section title="Add object">
          {Array.from(groups.entries()).map(([group, entries]) => (
            <div key={group} style={{ marginBottom: 8 }}>
              <div style={styles.groupHeader}>{group}</div>
              <div style={styles.catalogGrid}>
                {entries.map((e) => {
                  // Local profiles live in the user's localStorage and can
                  // always be edited — only **server** profiles need the
                  // localhost gate (because they save back to the repo).
                  const canEdit = activeRef.source === "local" || editor;
                  const enabled = e.available && canEdit;
                  return (
                    <button
                      key={e.kind}
                      style={{
                        ...styles.catalogItem,
                        opacity: enabled ? 1 : 0.35,
                        cursor: enabled ? "pointer" : "not-allowed",
                      }}
                      disabled={!enabled}
                      onClick={() => addKind(e.kind as SceneKind)}
                      title={
                        !e.available
                          ? `${e.label} (asset missing)`
                          : !canEdit
                            ? "Server profile is read-only outside localhost — switch to a local profile to edit"
                            : `Add ${e.label}`
                      }
                    >
                      {e.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </Section>
      </aside>

      {/* Right sidebar — settings + selection */}
      <aside style={styles.rightPanel}>
        {selectedIds.length > 1 ? (
          <Section title={`${selectedIds.length} objects selected`}>
            <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>
              Drag any to move all · Option-drag to clone all · R rotate · D duplicate · ⌫ delete
            </div>

            {/* Figma-style alignment + distribute bar.
                "Horizontal" = world X (screen-horizontal at default camera).
                "Vertical"   = world Z (depth on the floor plane).
                Distribute requires 3+ selected. */}
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>Align (horizontal)</div>
            <div style={styles.row}>
              <button title="Align left edges (world −X)"     style={styles.btn} onClick={() => runAlignOp(alignLeft)}>⇤</button>
              <button title="Align horizontal centres"        style={styles.btn} onClick={() => runAlignOp(alignCenterX)}>↔</button>
              <button title="Align right edges (world +X)"    style={styles.btn} onClick={() => runAlignOp(alignRight)}>⇥</button>
              <button
                title="Distribute horizontal centres equally (≥3)"
                style={{ ...styles.btn, opacity: selectedIds.length >= 3 ? 1 : 0.4 }}
                disabled={selectedIds.length < 3}
                onClick={() => runAlignOp(distributeHorizontally)}
              >⇿</button>
            </div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>Align (vertical)</div>
            <div style={styles.row}>
              <button title="Align near edges (world −Z)"     style={styles.btn} onClick={() => runAlignOp(alignTop)}>⤒</button>
              <button title="Align vertical centres"          style={styles.btn} onClick={() => runAlignOp(alignCenterZ)}>↕</button>
              <button title="Align far edges (world +Z)"      style={styles.btn} onClick={() => runAlignOp(alignBottom)}>⤓</button>
              <button
                title="Distribute vertical centres equally (≥3)"
                style={{ ...styles.btn, opacity: selectedIds.length >= 3 ? 1 : 0.4 }}
                disabled={selectedIds.length < 3}
                onClick={() => runAlignOp(distributeVertically)}
              >⇳</button>
            </div>
            <Slider
              label="Rotation (delta)"
              min={-Math.PI} max={Math.PI} step={Math.PI / 12}
              value={0}
              onChange={(v) => updateSelected({ rotation: v })}
              fmt={(v) => `${Math.round((v * 180) / Math.PI)}°`}
            />
            <Slider
              label="Scale (set all to)"
              min={0.3} max={3.0} step={0.05}
              value={selected?.scale ?? 1}
              onChange={(v) => updateSelected({ scale: v })}
            />
            <div style={styles.row}>
              <button style={styles.btn} onClick={removeSelected}>Delete all</button>
              <button style={styles.btn} onClick={() => setSelectedIds([])}>Clear selection</button>
            </div>
          </Section>
        ) : null}

        <Section title="Lighting">
          <div style={styles.row}>
            <button
              style={{ ...styles.btn, outline: lightingVersion === "v1" ? "1px solid var(--accent)" : "none" }}
              onClick={() => setLightingVersion("v1")}
              title="Original tuning"
            >
              V1
            </button>
            <button
              style={{ ...styles.btn, outline: lightingVersion === "v2" ? "1px solid var(--accent)" : "none" }}
              onClick={() => setLightingVersion("v2")}
              title="Deeper blacks, stronger silhouette rim — matches the matte hero render"
            >
              V2
            </button>
            <button
              style={{ ...styles.btn, outline: lightingVersion === "v3" ? "1px solid var(--accent)" : "none" }}
              onClick={() => setLightingVersion("v3")}
              title="Showroom-bright — chassis read as deep charcoal, highlights pop. Good for hero shots."
            >
              V3
            </button>
            <button
              style={{ ...styles.btn, outline: lightingVersion === "v4" ? "1px solid var(--accent)" : "none" }}
              onClick={() => setLightingVersion("v4")}
              title="Hero / product-shot — near-black bg, warm key, cool fill+rim, dimmed dot grid."
            >
              V4
            </button>
          </div>
        </Section>

        <Section title="Theme">
          <div style={styles.row}>
            <button
              style={{ ...styles.btn, outline: settings.theme === "dark" ? "1px solid var(--accent)" : "none" }}
              onClick={() => setSettings((p) => ({ ...p, theme: "dark" }))}
            >
              Dark
            </button>
            <button
              style={{ ...styles.btn, outline: settings.theme === "light" ? "1px solid var(--accent)" : "none" }}
              onClick={() => setSettings((p) => ({ ...p, theme: "light" }))}
            >
              Light
            </button>
          </div>
        </Section>

        <Section title="Display">
          <Tabs<DisplayTab>
            value={displayTab}
            onChange={setDisplayTab}
            options={[
              { id: "dots", label: "Dot grid" },
              { id: "guides", label: "Guides" },
              { id: "links", label: "Links" },
            ]}
          />
          {displayTab === "dots" && (
            <>
              <label style={styles.checkRow}>
                <input
                  type="checkbox"
                  checked={settings.dotGrid}
                  onChange={(e) => setSettings((p) => ({ ...p, dotGrid: e.target.checked }))}
                />
                Show dots
              </label>
              <Slider
                label="Dot spacing"
                min={0.5} max={5.0} step={0.1}
                value={settings.dotGridSpacing}
                onChange={(v) => setSettings((p) => ({ ...p, dotGridSpacing: v }))}
              />
              <Slider
                label="Dot size"
                min={DOT_SIZE_MIN} max={DOT_SIZE_MAX} step={0.0005}
                value={settings.dotGridSize ?? DOT_SIZE_DEFAULT}
                onChange={(v) => setSettings((p) => ({ ...p, dotGridSize: v }))}
              />
              <Slider
                label="Dot opacity"
                min={0} max={DOT_GRID_OPACITY_MAX} step={0.02}
                value={dotGridOpacityValue(settings)}
                onChange={(v) =>
                  setSettings((p) =>
                    p.theme === "dark"
                      ? { ...p, dotGridOpacityDark: v }
                      : { ...p, dotGridOpacityLight: v },
                  )
                }
                fmt={formatDotOpacity}
              />
            </>
          )}
          {displayTab === "guides" && (
            <>
              <label style={styles.checkRow}>
                <input
                  type="checkbox"
                  checked={!!settings.gridLines}
                  onChange={(e) => setSettings((p) => ({
                    ...defaultGuideSettings(),
                    ...p,
                    gridLines: e.target.checked,
                  }))}
                />
                Show guides
              </label>
              {settings.gridLines && (
                <>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      style={styles.btn}
                      onClick={() => {
                        pushSnapshot();
                        const id = Math.random().toString(36).slice(2, 10);
                        const coord = Math.round((settingsRef.current.camera.targetZ ?? 0) * 2) / 2;
                        setSettings((p) => ({ ...p, guides: [...(p.guides ?? []), { id, axis: "x", coord }] }));
                        setSelectedGuideId(id);
                      }}
                    >+ Horizontal</button>
                    <button
                      style={styles.btn}
                      onClick={() => {
                        pushSnapshot();
                        const id = Math.random().toString(36).slice(2, 10);
                        const coord = Math.round((settingsRef.current.camera.targetX ?? 0) * 2) / 2;
                        setSettings((p) => ({ ...p, guides: [...(p.guides ?? []), { id, axis: "z", coord }] }));
                        setSelectedGuideId(id);
                      }}
                    >+ Vertical</button>
                  </div>
                  <Slider
                    label="Line opacity"
                    min={0} max={1} step={0.02}
                    value={settings.theme === "dark"
                      ? (settings.gridLinesOpacityDark ?? 0.55)
                      : (settings.gridLinesOpacityLight ?? 0.65)}
                    onChange={(v) => setSettings((p) =>
                      p.theme === "dark"
                        ? { ...p, gridLinesOpacityDark: v }
                        : { ...p, gridLinesOpacityLight: v },
                    )}
                  />
                  <Slider
                    label="Dot length"
                    min={0.01} max={0.6} step={0.01}
                    value={settings.gridLinesDashSize ?? 0.06}
                    onChange={(v) => setSettings((p) => ({ ...p, gridLinesDashSize: v }))}
                    fmt={(v) => `${v.toFixed(2)} m`}
                  />
                  <Slider
                    label="Dot gap"
                    min={0.02} max={1.5} step={0.02}
                    value={settings.gridLinesGapSize ?? 0.22}
                    onChange={(v) => setSettings((p) => ({ ...p, gridLinesGapSize: v }))}
                    fmt={(v) => `${v.toFixed(2)} m`}
                  />
                  <label style={styles.checkRow}>
                    <input
                      type="checkbox"
                      checked={settings.gridLinesShowCrosses !== false}
                      onChange={(e) => setSettings((p) => ({ ...p, gridLinesShowCrosses: e.target.checked }))}
                    />
                    Crosses at intersections
                  </label>
                  {settings.gridLinesShowCrosses !== false && (
                    <Slider
                      label="Cross opacity"
                      min={0} max={1} step={0.02}
                      value={settings.theme === "dark"
                        ? (settings.gridLinesCrossOpacityDark ?? 0.75)
                        : (settings.gridLinesCrossOpacityLight ?? 0.85)}
                      onChange={(v) => setSettings((p) =>
                        p.theme === "dark"
                          ? { ...p, gridLinesCrossOpacityDark: v }
                          : { ...p, gridLinesCrossOpacityLight: v },
                      )}
                    />
                  )}
                  {selectedGuideId && (
                    <button
                      style={styles.btn}
                      onClick={() => {
                        pushSnapshot();
                        setSettings((p) => ({
                          ...p,
                          guides: (p.guides ?? []).filter((g) => g.id !== selectedGuideId),
                        }));
                        setSelectedGuideId(null);
                      }}
                    >Delete selected guide</button>
                  )}
                  <div style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>
                    Click a guide to select. Drag to move (Shift = no snap). Alt-drag duplicates. Backspace deletes.
                  </div>
                </>
              )}
            </>
          )}
          {displayTab === "links" && (
            <>
              <Slider
                label="Thickness"
                min={0.005} max={0.5} step={0.005}
                value={settings.linkThickness}
                onChange={(v) => setSettings((p) => ({ ...p, linkThickness: v }))}
              />
              <Slider
                label="Cable opacity"
                min={0} max={1} step={0.02}
                value={settings.linkCableOpacity}
                onChange={(v) => setSettings((p) => ({ ...p, linkCableOpacity: v }))}
              />
              <Slider
                label="Power opacity"
                min={0} max={1} step={0.02}
                value={settings.linkStripOpacity}
                onChange={(v) => setSettings((p) => ({ ...p, linkStripOpacity: v }))}
              />
              <Slider
                label="Power speed"
                min={0} max={1} step={0.05}
                value={settings.linkSpeed}
                onChange={(v) => setSettings((p) => ({ ...p, linkSpeed: v }))}
              />
              <label style={styles.checkRow}>
                <input
                  type="checkbox"
                  checked={settings.linkPower}
                  onChange={(e) => setSettings((p) => ({ ...p, linkPower: e.target.checked }))}
                />
                Power on (animate flow)
              </label>
              <label style={styles.checkRow}>
                <input
                  type="checkbox"
                  checked={settings.straightLinks}
                  onChange={(e) => setSettings((p) => ({ ...p, straightLinks: e.target.checked }))}
                />
                Straight (no stub)
              </label>
            </>
          )}
        </Section>

        <Section title="Camera">
          <div style={{ ...styles.kvRow, fontSize: 10, lineHeight: 1.5 }}>
            <span style={{ color: "var(--muted)" }}>Nav</span>
            <span style={{ color: "var(--muted)", textAlign: "right" }}>
              RMB = orbit · Cmd/Ctrl-drag empty = pan · scroll = zoom
            </span>
          </div>
          {/* Six axial views — each "row" is one viewing axis pair. */}
          <div style={styles.row}>
            <button style={styles.btn} onClick={() => setView("front")} title="Front (looking down −Z)">Front</button>
            <button style={styles.btn} onClick={() => setView("back")}  title="Back (looking down +Z)">Back</button>
          </div>
          <div style={styles.row}>
            <button style={styles.btn} onClick={() => setView("left")}  title="Left (looking down +X)">Left</button>
            <button style={styles.btn} onClick={() => setView("right")} title="Right (looking down −X)">Right</button>
          </div>
          <div style={styles.row}>
            <button style={styles.btn} onClick={() => setView("top")}    title="Top (looking down −Y)">Top</button>
            <button style={styles.btn} onClick={() => setView("bottom")} title="Bottom (looking up +Y)">Bottom</button>
          </div>

          {/* Rotate the current view 90° / 180° around the orbit pivot.
              Useful when an axial preset lands you upside-down — one click
              re-orients without leaving the current axis. */}
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 6 }}>Rotate camera</div>
          <div style={styles.row}>
            <button style={styles.btn} onClick={() => rotateView(-Math.PI / 2)} title="Rotate 90° counter-clockwise">↺ 90°</button>
            <button style={styles.btn} onClick={() => rotateView( Math.PI / 2)} title="Rotate 90° clockwise">↻ 90°</button>
            <button style={styles.btn} onClick={() => rotateView( Math.PI)}     title="Flip 180°">⤺ 180°</button>
          </div>

          {/* Iso quadrants — start positions for typical iso compositions. */}
          <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 6 }}>Isometric</div>
          <div style={{ ...styles.row, marginTop: 0 }}>
            <button style={styles.btn} onClick={() => setView("iso_nw")}>Iso NW</button>
            <button style={styles.btn} onClick={() => setView("iso_ne")}>Iso NE</button>
          </div>
          <div style={styles.row}>
            <button style={styles.btn} onClick={() => setView("iso_sw")}>Iso SW</button>
            <button style={styles.btn} onClick={() => setView("iso_se")}>Iso SE</button>
          </div>
          <Slider
            label="FOV"
            min={20} max={80} step={0.5}
            value={settings.camera.fov}
            onChange={(v) => setSettings((p) => ({ ...p, camera: { ...p.camera, fov: v } }))}
          />
          <Slider
            label="Distance"
            min={2} max={200} step={0.5}
            value={settings.camera.distance}
            onChange={(v) => setSettings((p) => ({ ...p, camera: { ...p.camera, distance: v } }))}
          />
          <Slider
            label="Height"
            min={CAMERA_PITCH_MIN} max={CAMERA_PITCH_MAX} step={0.01}
            value={settings.camera.pitch}
            onChange={(v) => setSettings((p) => ({ ...p, camera: { ...p.camera, pitch: v } }))}
            fmt={formatCameraHeight}
          />
          <div style={styles.row}>
            <button style={styles.btn} onClick={recenterPivot}>Pivot to origin</button>
            <button style={styles.btn} onClick={resetCamera}>Reset camera</button>
          </div>
        </Section>

        <Section title="Float shadow">
          <label style={styles.checkRow}>
            <input
              type="checkbox"
              checked={settings.floatEnabled}
              onChange={(e) => setSettings((p) => ({ ...p, floatEnabled: e.target.checked }))}
            />
            Float objects
          </label>
          {settings.floatEnabled && (
            <>
              <Slider
                label="Height"
                min={0.1} max={1.5} step={0.05}
                value={settings.floatHeight}
                onChange={(v) => setSettings((p) => ({ ...p, floatHeight: v }))}
              />
              <Slider
                label="Shadow size"
                min={1} max={10} step={0.1}
                value={settings.floatShadowSize}
                onChange={(v) => setSettings((p) => ({ ...p, floatShadowSize: v }))}
              />
              <Slider
                label="Shadow opacity"
                min={0} max={1} step={0.02}
                value={settings.floatShadowOpacity}
                onChange={(v) => setSettings((p) => ({ ...p, floatShadowOpacity: v }))}
              />
            </>
          )}
        </Section>
      </aside>
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================

function groupCatalog(catalog: CatalogEntry[]): Map<string, CatalogEntry[]> {
  const m = new Map<string, CatalogEntry[]>();
  const order = ["AI", "Infra", "Network", "Power", "Site", "Brand"];
  for (const g of order) m.set(g, []);
  for (const e of catalog) {
    if (!m.has(e.group)) m.set(e.group, []);
    m.get(e.group)!.push(e);
  }
  return addVirtualRackRowEntry(addVirtualBrandEntries(m));
}

// ============================================================
// Subcomponents
// ============================================================

function Branding() {
  return (
    <div style={styles.branding}>
      <span style={{ fontWeight: 600 }}>OneQode</span>
      <span style={{ color: "var(--muted)" }}>Scene builder</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>{children}</div>
    </div>
  );
}

// Tabs lives in its own module (./Tabs) so both builders import it
// without forming a circular dependency.

// Shared profile picker. Used by both SceneBuilder and IsometricBuilder.
type LocalListEntry = { slug: string; name: string };
function ProfilePicker(props: {
  viewKind: "scene" | "iso";
  activeRef: ProfileRef;
  serverList: Array<{ slug: string; kind: "scene" | "iso" }>;
  localList: LocalListEntry[];
  editor: boolean;
  saveStatus: "idle" | "saving" | "saved" | "error";
  onSwitch: (ref: ProfileRef) => void;
  onNewProfile: () => void;
  /** Save the *current* scene as a new local profile. Different from
   *  onNewProfile (which creates a blank scene). */
  onDuplicateToLocal?: () => void;
  onDelete: (ref: ProfileRef) => void;
  onRenameLocal: (slug: string) => void;
  onExport: () => void;
  onImportClick: () => void;
}) {
  const {
    activeRef, serverList, localList, editor, saveStatus,
    onSwitch, onNewProfile, onDuplicateToLocal, onDelete, onRenameLocal,
    onExport, onImportClick,
  } = props;

  // Pretty-print server slugs. scene-main → Main (Reiss); scene-foo-bar → Foo bar.
  function nameForServer(slug: string): string {
    if (slug === "scene-main" || slug === "iso-main") return "Main (Reiss)";
    const stripped = slug.replace(/^(scene|iso)-/, "");
    return stripped.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  const status =
    saveStatus === "saving" ? "Saving…"
    : saveStatus === "saved" ? "Saved"
    : saveStatus === "error" ? "Save failed"
    : (activeRef.source === "server" && !editor) ? "Read-only"
    : activeRef.source === "server" ? "Editor mode (localhost)"
    : "Local profile";

  return (
    <>
      <div style={{ fontSize: 11, color: "var(--muted)", marginBottom: 4 }}>{status}</div>
      {!editor && activeRef.source === "server" && (
        <div style={{ fontSize: 10, color: "var(--muted)", lineHeight: 1.4, marginBottom: 6 }}>
          You can drag and edit this scene, but changes won't save. Use{" "}
          <strong style={{ color: "var(--fg)" }}>+ Duplicate to local</strong>{" "}
          to keep your edits in this browser.
        </div>
      )}

      {serverList.length > 0 && (
        <>
          <div style={styles.groupHeader}>Server</div>
          <div style={styles.profileList}>
            {serverList.map((e) => {
              const name = nameForServer(e.slug);
              const ref = makeServerRef(props.viewKind, e.slug, name);
              const active = activeRef.source === "server" && activeRef.slug === e.slug;
              const canDelete = editor && e.slug !== "scene-main" && e.slug !== "iso-main";
              return (
                <div
                  key={e.slug}
                  style={{
                    ...styles.profileRow,
                    outline: active ? "1px solid var(--accent)" : "1px solid transparent",
                  }}
                >
                  <button
                    style={styles.profileName}
                    onClick={() => onSwitch(ref)}
                    title={e.slug}
                  >
                    {name}
                  </button>
                  {canDelete && (
                    <button style={styles.miniBtn} onClick={() => onDelete(ref)} title="Delete server file">×</button>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {localList.length > 0 && (
        <>
          <div style={{ ...styles.groupHeader, marginTop: 8 }}>Local (this browser)</div>
          <div style={styles.profileList}>
            {localList.map((e) => {
              const ref = makeLocalRef(props.viewKind, e.slug, e.name);
              const active = activeRef.source === "local" && activeRef.slug === e.slug;
              return (
                <div
                  key={e.slug}
                  style={{
                    ...styles.profileRow,
                    outline: active ? "1px solid var(--accent)" : "1px solid transparent",
                  }}
                >
                  <button
                    style={styles.profileName}
                    onClick={() => onSwitch(ref)}
                    onDoubleClick={() => onRenameLocal(e.slug)}
                    title="Click: switch · Dbl-click: rename"
                  >
                    {e.name}
                  </button>
                  <button style={styles.miniBtn} onClick={() => onDelete(ref)} title="Delete">×</button>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div style={{ ...styles.row, marginTop: 8, flexWrap: "wrap", gap: 4 }}>
        <button
          style={styles.btn}
          onClick={onNewProfile}
          title={editor
            ? "Create a new (empty) server profile"
            : "Create a new (empty) local profile"}
        >
          + New
        </button>
        {onDuplicateToLocal && (
          <button
            style={styles.btn}
            onClick={onDuplicateToLocal}
            title="Save a copy of the current scene as a new local profile"
          >
            + Duplicate
          </button>
        )}
        <button style={styles.btn} onClick={onExport}>Export JSON</button>
        <button style={styles.btn} onClick={onImportClick}>Import JSON</button>
      </div>
    </>
  );
}

function Slider({
  label, min, max, step, value, onChange, fmt,
}: {
  label: string; min: number; max: number; step: number; value: number;
  onChange: (v: number) => void; fmt?: (v: number) => string;
}) {
  return (
    <label style={styles.sliderRow}>
      <span style={styles.sliderLabel}>
        {label}
        <span style={styles.sliderValue}>{fmt ? fmt(value) : value.toFixed(2)}</span>
      </span>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={styles.range}
      />
    </label>
  );
}

function BrandMarkControls(props: {
  brandMark: "icon" | "logo" | null;
  brandSide: "front" | "back" | "left" | "right" | "top" | null;
  onChange: (patch: { brandMark?: "icon" | "logo" | null; brandSide?: "front" | "back" | "left" | "right" | "top" | null }) => void;
}) {
  const { brandMark, brandSide, onChange } = props;
  const radioBtn = (label: string, value: "icon" | "logo" | null) => (
    <label key={String(value)} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
      <input
        type="radio"
        name="brand-mark"
        checked={brandMark === value}
        onChange={() => {
          // Picking a mark for the first time defaults to "front".
          if (value && !brandSide) onChange({ brandMark: value, brandSide: "top" });
          else if (!value) onChange({ brandMark: null, brandSide: null });
          else onChange({ brandMark: value });
        }}
      />
      {label}
    </label>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, marginTop: 4 }}>
      <div style={{ color: "var(--muted)" }}>Brand mark</div>
      <div style={{ display: "flex", gap: 8 }}>
        {radioBtn("None", null)}
        {radioBtn("Icon", "icon")}
        {radioBtn("Logo", "logo")}
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginTop: 2, opacity: brandMark ? 1 : 0.45 }}>
        Side
        <select
          value={brandSide ?? "top"}
          disabled={!brandMark}
          onChange={(e) => onChange({ brandSide: e.target.value as "front" | "back" | "left" | "right" | "top" })}
          style={{ flex: 1, fontSize: 11 }}
        >
          <option value="front">Front</option>
          <option value="back">Back</option>
          <option value="left">Left</option>
          <option value="right">Right</option>
          <option value="top">Top</option>
        </select>
      </label>
    </div>
  );
}

function ErrorScreen({ msg }: { msg: string }) {
  return (
    <div style={styles.errScreen}>
      <h3>Failed to load</h3>
      <p style={{ color: "#ff8a8a" }}>{msg}</p>
      <p style={{ color: "var(--muted)", marginTop: 8 }}>
        Did you run <code>pnpm bake</code>?
      </p>
    </div>
  );
}

function Loading() {
  return <div style={styles.errScreen}>Loading manifest…</div>;
}

function HelpDialog({ onClose }: { onClose: () => void }) {
  // Close on Escape.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div style={styles.helpBackdrop} onClick={onClose}>
      <div style={styles.helpDialog} onClick={(e) => e.stopPropagation()}>
        <div style={styles.helpHeader}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Navigation & shortcuts</span>
          <button style={styles.helpClose} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={styles.helpBody}>
          <HelpSection title="Camera">
            <HelpRow keys={["Right-mouse", "drag"]} desc="Orbit around target" />
            <HelpRow keys={["Middle-mouse", "drag"]} desc="Pan the target" />
            <HelpRow keys={["Cmd / Ctrl + Left", "drag empty canvas"]} desc="Pan the target" />
            <HelpRow keys={["Alt + Left", "drag empty canvas"]} desc="Orbit (trackpad-friendly)" />
            <HelpRow keys={["Shift + Alt + Left", "drag empty canvas"]} desc="Pan (trackpad-friendly)" />
            <HelpRow keys={["Scroll", "/ pinch"]} desc="Dolly (zoom in/out)" />
          </HelpSection>

          <HelpSection title="View presets">
            <HelpRow keys={["1"]} desc="Front view" />
            <HelpRow keys={["3"]} desc="Right view" />
            <HelpRow keys={["7"]} desc="Top view" />
            <HelpRow keys={["0"]} desc="Iso (3-quarter)" />
            <HelpRow keys={["F", "or", "Numpad ."]} desc="Frame the selected object" />
          </HelpSection>

          <HelpSection title="Objects">
            <HelpRow keys={["Click"]} desc="Select" />
            <HelpRow keys={["Drag"]} desc="Move on the ground plane" />
            <HelpRow keys={["Option / Alt", "drag"]} desc="Duplicate selected objects and move the copies" />
            <HelpRow keys={["Shift + drag"]} desc="Axis-locked move" />
            <HelpRow keys={["R"]} desc="Rotate selected by 90°" />
            <HelpRow keys={["D"]} desc="Duplicate selected" />
            <HelpRow keys={["Backspace", "/", "Delete"]} desc="Remove selected" />
          </HelpSection>

          <HelpSection title="Profiles">
            <HelpRow keys={["Click"]} desc="Switch to that profile" />
            <HelpRow keys={["Double-click"]} desc="Rename" />
            <HelpRow keys={["Export / Import"]} desc="JSON round-trips with oq-images saves" />
          </HelpSection>
        </div>

        <div style={styles.helpFooter}>
          Press <span style={styles.kbd}>Esc</span> or click outside to close.
        </div>
      </div>
    </div>
  );
}

function HelpSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={styles.helpSectionTitle}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>{children}</div>
    </div>
  );
}

function HelpRow({ keys, desc }: { keys: string[]; desc: string }) {
  return (
    <div style={styles.helpRow}>
      <div style={styles.helpKeys}>
        {keys.map((k, i) => (
          <span key={i} style={styles.kbd}>{k}</span>
        ))}
      </div>
      <span style={{ color: "var(--fg)" }}>{desc}</span>
    </div>
  );
}

// ============================================================
// Styles
// ============================================================

const PANEL_BG = "rgba(15, 15, 18, 0.86)";
const PANEL_BORDER = "1px solid #1f1f24";

const styles: Record<string, React.CSSProperties> = {
  root: { position: "absolute", inset: 0, overflow: "hidden" },
  canvasMount: { position: "absolute", inset: 0 },
  topBar: {
    position: "absolute", top: 12, left: 12, right: 12, display: "flex",
    alignItems: "center", gap: 12, pointerEvents: "none",
  },
  branding: { display: "flex", gap: 8, fontSize: 13, pointerEvents: "auto" },
  spacer: { flex: 1 },
  selectionLabel: {
    position: "absolute",
    transform: "translate(-50%, -100%)",
    background: "rgba(15, 15, 18, 0.92)",
    color: "var(--fg)",
    border: "1px solid #2a2a30",
    padding: "3px 8px",
    borderRadius: 3,
    fontSize: 11,
    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
    letterSpacing: 0.3,
    pointerEvents: "none",
    whiteSpace: "nowrap",
    zIndex: 5,
  },
  linkingBanner: {
    position: "absolute", top: 50, left: "50%", transform: "translateX(-50%)",
    background: "rgba(20, 76, 232, 0.85)", padding: "6px 12px", borderRadius: 4,
    fontSize: 12, pointerEvents: "none",
  },
  leftPanel: {
    position: "absolute", top: 56, left: 12, bottom: 12, width: 240,
    overflowY: "auto", background: PANEL_BG, border: PANEL_BORDER, borderRadius: 6,
    padding: 12, fontSize: 12,
  },
  rightPanel: {
    position: "absolute", top: 56, right: 12, bottom: 12, width: 280,
    overflowY: "auto", background: PANEL_BG, border: PANEL_BORDER, borderRadius: 6,
    padding: 12, fontSize: 12,
  },
  section: {
    marginBottom: 16, paddingBottom: 12,
    borderBottom: "1px solid var(--line)",
  },
  sectionTitle: {
    fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5,
    color: "var(--muted)", marginBottom: 8,
  },
  groupHeader: {
    fontSize: 10, textTransform: "uppercase", letterSpacing: 1,
    color: "var(--muted)", marginBottom: 4,
  },
  catalogGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 },
  catalogItem: {
    background: "var(--bg)", color: "var(--fg)", border: PANEL_BORDER,
    padding: "6px 4px", borderRadius: 3, fontSize: 11, cursor: "pointer",
    textAlign: "left",
  },
  profileList: { display: "flex", flexDirection: "column", gap: 4, marginBottom: 6 },
  profileRow: {
    display: "flex", alignItems: "center", gap: 4,
    padding: "4px 6px", borderRadius: 3, background: "var(--bg)",
  },
  profileName: {
    flex: 1, background: "transparent", color: "var(--fg)", border: "none",
    textAlign: "left", cursor: "pointer", padding: 0, fontSize: 12,
  },
  row: { display: "flex", gap: 4 },
  btn: {
    background: "var(--bg)", color: "var(--fg)", border: PANEL_BORDER,
    padding: "6px 10px", borderRadius: 3, cursor: "pointer", fontSize: 11,
    pointerEvents: "auto",
  },
  miniBtn: {
    background: "transparent", color: "var(--muted)", border: "none",
    cursor: "pointer", fontSize: 14, padding: "2px 4px",
  },
  input: {
    flex: 1, background: "var(--bg)", color: "var(--fg)", border: PANEL_BORDER,
    padding: "4px 6px", fontSize: 12, borderRadius: 3,
  },
  kvRow: { display: "flex", justifyContent: "space-between", color: "var(--muted)" },
  checkRow: { display: "flex", alignItems: "center", gap: 6 },
  sliderRow: { display: "flex", flexDirection: "column", gap: 2 },
  sliderLabel: {
    display: "flex", justifyContent: "space-between",
    color: "var(--muted)", fontSize: 11,
  },
  sliderValue: { color: "var(--fg)" },
  range: { width: "100%" },
  errScreen: {
    position: "absolute", inset: 0, display: "flex", alignItems: "center",
    justifyContent: "center", flexDirection: "column", gap: 8,
    color: "var(--fg)", padding: 32, textAlign: "center",
  },
  helpBtn: {
    background: "var(--bg)", color: "var(--fg)", border: "1px solid #1f1f24",
    width: 26, height: 26, borderRadius: 13, cursor: "pointer", fontSize: 13,
    fontWeight: 600, pointerEvents: "auto",
  },
  readOnlyChip: {
    background: "rgba(160, 160, 170, 0.12)",
    color: "var(--muted)",
    border: "1px solid #2a2a30",
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 10,
    letterSpacing: 1,
    textTransform: "uppercase",
    pointerEvents: "auto",
  },
  helpBackdrop: {
    position: "absolute", inset: 0, background: "rgba(0,0,0,0.55)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 100, pointerEvents: "auto",
  },
  helpDialog: {
    background: "#0f0f12", border: "1px solid #1f1f24", borderRadius: 8,
    width: "min(560px, 92vw)", maxHeight: "86vh",
    display: "flex", flexDirection: "column",
    boxShadow: "0 24px 60px rgba(0,0,0,0.55)",
  },
  helpHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "14px 16px", borderBottom: "1px solid #1f1f24",
  },
  helpClose: {
    background: "transparent", color: "var(--muted)", border: "none",
    fontSize: 22, lineHeight: 1, cursor: "pointer", padding: 0, width: 24, height: 24,
  },
  helpBody: {
    overflowY: "auto", padding: "12px 16px", flex: 1,
  },
  helpFooter: {
    padding: "10px 16px", borderTop: "1px solid #1f1f24",
    color: "var(--muted)", fontSize: 11,
  },
  helpSectionTitle: {
    fontSize: 11, textTransform: "uppercase", letterSpacing: 1.5,
    color: "var(--muted)", marginBottom: 6,
  },
  helpRow: {
    display: "grid", gridTemplateColumns: "200px 1fr", alignItems: "center",
    gap: 12, fontSize: 12,
  },
  helpKeys: { display: "flex", flexWrap: "wrap", gap: 4 },
  kbd: {
    display: "inline-block",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid #2a2a30",
    borderRadius: 3,
    padding: "1px 6px",
    fontSize: 11,
    fontFamily: "ui-monospace, Menlo, Consolas, monospace",
    color: "var(--fg)",
  },
};
